import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  endTranscriptionSession,
  startTranscriptionSession,
} from '../lib/recorder/TranscriptionSessionUtils';
import { acquireRecordingStream } from '../lib/recorder/acquireRecordingStream';
import type { AudioMixerResult } from '../lib/recorder/acquireRecordingStream';
import { useAccessToken, useTemplatesList, useIsAuthenticated } from '@features/user';
import { startNoteProcessingSession } from '../lib/recorder/NoteProcessingSessionUtils';
import { useLocalStorageState } from '../hooks/useLocalStorageState';
import WebRecordingService from '../services/WebRecordingService';
import { useAudioDataContext } from './AudioDataContext';
import { indexedDBManager } from '../services/IndexDBService';
import APIService, { FRONTEND_RECORDING_APP_VERSION, API_BASE_URL } from '../services/APIService';
import { UploadError } from '@shared/Errors';
import { NoteInformation, RecordingStatus } from '@shared/Types/RecordingTypes';
import { useUIState } from './uiState';
import { RecordingChunk, RecordingSession } from '../lib/recorder/RecordingSession';
import SentryService from '../services/SentryService';
import { useSnackbar } from 'notistack';
import tabCommunicator from '../lib/tabCommunicator';
import { StorageCleanupDialog } from '../components/StorageCleanupDialog';
import { Button } from '@mui/material';
import { AudioInputDeviceService, normalizedToMediaDeviceInfo } from '@services/audio';
import type {
  HealthState,
  DeviceDiff,
  NormalizedDevice,
  PermissionResult as AudioPermissionResult,
} from '@services/audio';
import { useEventTracking } from '@services/statsig';
import { EventNames } from '@services/statsig/constants';
import { getStatsigClientInstance } from '@services/statsig/trackNoteTelemetryToStatsig';
import { StorageService } from '@services/storage';
import { UploadService } from '@services/upload';
import { RecordingService, DEFAULT_TIMESLICE_MS } from '@services/recording';
import type { RecordingEventMap } from '@services/recording';
import {
  SessionCompletionService,
  SessionManifest,
  SessionRecoveryService,
} from '@services/session';
import { DailyCallService, DeviceBridgeService } from '@services/dailycall';
import type { DailyCallEventMap } from '@services/dailycall';
import type { PipelineError } from './utils/classifyPipelineError';
import { classifyPipelineError } from './utils/classifyPipelineError';
import type { PipelineErrorSource } from './utils/classifyPipelineError';

// Define the interface for the context
interface RecordingContextType {
  currentTranscriptionSessionId: string;
  recordingStatus: RecordingStatus;
  noteInformation: NoteInformation;
  setNoteInformation: React.Dispatch<React.SetStateAction<NoteInformation>>;
  setUseSharedMedia: React.Dispatch<React.SetStateAction<boolean>>;
  useSharedMedia: boolean;
  noAudioStatus: boolean;
  checkMicrophonePermission: () => Promise<boolean>;
  setSelectedMicrophone: (selectedMicrophone: MediaDeviceInfo) => void;
  setNoAudioStatus: (status: boolean) => void;
  getAvailableMicrophones: () => void;
  initiateRecording: () => Promise<boolean>;
  initiateEndRecording: () => Promise<boolean>;
  sendRecording: () => Promise<{ note_id: string; thread_id?: string } | undefined>;
  selectedMicrophone?: MediaDeviceInfo;
  availableMicrophones?: MediaDeviceInfo[];
  microphonePermissionGranted?: boolean;
  pauseRecording: () => void; // pause
  resumeRecording: () => Promise<boolean>; // resume
  initiateReupload: () => Promise<boolean>;
  sharedAudioEnabled?: boolean;
  setSharedAudioEnabled?: (enabled: boolean) => void;
  deleteRecordingHelper: () => Promise<boolean>;
  startTimer: () => void;
  stopTimer: () => void;
  resetTimer: () => void;
  elapsedTime: number;
  cleanUpSession: () => void;
  softResetForNavigation: () => void;
  releaseMicrophone: () => void;
  lowDataMode: boolean;
  sendMessageToExtension: (message: any) => void;
  setShouldBeCleanedUp: (value: boolean) => void;
  allowRecorderControl: boolean;
  takeOverRecording: () => Promise<boolean>;
  rehydrateStateFromLocalStorage: () => void;
  checkRecordingOwnership: () => void;
  storagePersisted: boolean | null;
  checkStoragePersistence: () => Promise<boolean>;
  getRecordingTimes: () => { startTime: Date; endTime: Date; elapsedTime: number } | null;
  startMicTest: () => Promise<boolean>;
  stopMicTest: () => void;
  isMicTesting: boolean;
  onS3UploadError?: (error: string) => void;
  setOnS3UploadError: (callback: ((error: string) => void) | undefined) => void;
  onRecordingFinalized?: (transcriptionSessionId: string, elapsedTime: number) => Promise<void>;
  setOnRecordingFinalized: (
    callback: ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>) | undefined,
  ) => void;
  setOnRecordingStopped: (
    callback: ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>) | undefined,
  ) => void;
  requestRemoteStop: () => Promise<{ success: boolean; reason?: string }>;
  requestRemotePause: () => Promise<{ success: boolean; reason?: string }>;
  requestRemoteResume: () => Promise<{ success: boolean; reason?: string }>;
  remoteStopPending: boolean;
  audioHealthState: HealthState;
  releaseAudioStream: () => void;
  needsMicPermission: boolean;
  enableAudioDeviceService: (enabled: boolean) => void;
  enableStorageService: (enabled: boolean) => void;
  enableUploadService: (enabled: boolean) => void;
  enableRecordingService: (enabled: boolean) => void;
  enableSessionCompletionService: (enabled: boolean) => void;
  setRecordingEncounterId: (id: string | null) => void;
  getRecordingEncounterId: () => string | null;
  setTelehealthSession: (config: { dailyCallService: DailyCallService; roomUrl: string; token: string } | null) => void;
  cleanupTelehealthSession: () => void;
  /** Read-only access to AudioInputDeviceService for device enumeration/selection (telehealth device UIs) */
  audioInputDeviceService: AudioInputDeviceService | null;
  // Pipeline error state (V5 init failures surfaced to users)
  resetRecordingState: () => void;
  pipelineError: PipelineError | null;
  retryPipelineInit: () => void;
  // Read-only service instances for useSyncExternalStore hooks (UI components)
  sessionCompletionService: SessionCompletionService | null;
  sessionRecoveryService: SessionRecoveryService | null;
  uploadService: UploadService | null;
  recordingService: RecordingService | null;
  storageService: StorageService | null;
}

const RECORDING_CHUNK_LENGTH_SECONDS = 90; // 90 secs
const LOW_DATA_CHUNK_LENGTH_SECTIONS = 50; // 50 secs
const MAX_RECORDING_LENGTH_SECONDS = 60 * 60 * 4; // 4 hours

// Create the Recording context
const RecordingContext = createContext<RecordingContextType | undefined>(undefined);

let chunkPosition = 0;

// Custom hook to use the Recording context
export function useRecordingContext(): RecordingContextType {
  const context = useContext(RecordingContext);

  if (!context) {
    throw new Error('useRecordingContext must be used within an RecordingContextProvider');
  }
  return context;
}

/** Classify recording start errors for Statsig tracking. */
function classifyStartError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes('Not ready to record')) return 'not_ready';
  if (msg.includes('Cannot start recording in state:')) return 'recording_service_state_error';
  if (msg.includes('Failed to get transcription session ID')) return 'api_session_failed';
  if (msg.includes('Failed to create media recorder')) return 'media_recorder_failed';
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError') return 'mic_permission_denied';
    if (err.name === 'NotFoundError') return 'mic_not_found';
    if (err.name === 'NotReadableError') return 'mic_not_readable';
  }
  return 'unknown';
}

/** Subscribe to DailyCallService track events and wire remote audio into the mixer. */
function wireDailyTrackEvents(
  dailyCallService: DailyCallService,
  mixerRef: React.MutableRefObject<AudioMixerResult | null>,
): () => void {
  const onTrackStarted = ({ sessionId, type, track }: DailyCallEventMap['trackStarted']) => {
    if (type !== 'audio') return;
    // Filter local tracks: state is updated before emit, so remote tracks are in the map
    if (!dailyCallService.getSnapshot().remoteAudioTracks.has(sessionId)) return;
    mixerRef.current?.connectRemoteTrack(sessionId, track);
    SentryService.addBreadcrumb({ category: 'telehealth.mixer', message: `Connected remote audio: ${sessionId}`, level: 'info' });
  };

  const onTrackStopped = ({ sessionId, type }: DailyCallEventMap['trackStopped']) => {
    if (type !== 'audio') return;
    // disconnectRemoteTrack is idempotent — safe to call for local (no-op)
    mixerRef.current?.disconnectRemoteTrack(sessionId);
    SentryService.addBreadcrumb({ category: 'telehealth.mixer', message: `Disconnected remote audio: ${sessionId}`, level: 'info' });
  };

  const onParticipantLeft = ({ sessionId }: DailyCallEventMap['participantLeft']) => {
    mixerRef.current?.disconnectRemoteTrack(sessionId);
    SentryService.addBreadcrumb({ category: 'telehealth.mixer', message: `Participant left: ${sessionId}`, level: 'info' });
  };

  const onStatusChanged = ({ status, prev }: DailyCallEventMap['statusChanged']) => {
    if (prev === 'connected' && status === 'idle') {
      // Call ended — disconnect all remote audio, recording continues
      const snapshot = dailyCallService.getSnapshot();
      snapshot.remoteAudioTracks.forEach((_, participantId) => {
        mixerRef.current?.disconnectRemoteTrack(participantId);
      });
      SentryService.addBreadcrumb({ category: 'telehealth.mixer', message: 'Call ended — disconnected all remote audio', level: 'info' });
    }
  };

  dailyCallService.on('trackStarted', onTrackStarted);
  dailyCallService.on('trackStopped', onTrackStopped);
  dailyCallService.on('participantLeft', onParticipantLeft);
  dailyCallService.on('statusChanged', onStatusChanged);

  return () => {
    dailyCallService.off('trackStarted', onTrackStarted);
    dailyCallService.off('trackStopped', onTrackStopped);
    dailyCallService.off('participantLeft', onParticipantLeft);
    dailyCallService.off('statusChanged', onStatusChanged);
  };
}

// Define the props for the RecordingContextProvider
interface RecordingContextProviderProps {
  children: ReactNode;
}

// Create the RecordingContextProvider component
export const RecordingContextProvider: React.FC<RecordingContextProviderProps> = ({ children }) => {
  const { getAccessToken } = useAccessToken();
  const isAuthenticated = useIsAuthenticated();
  const templatesList = useTemplatesList({
    skip: !isAuthenticated,
  });
  const { setAudioData, setPaused } = useAudioDataContext();
  const { enqueueSnackbar } = useSnackbar();
  const { state: uiState, showAlertBanner } = useUIState();
  const { platform, loadedInChromeExtension } = uiState;

  // Define state variables
  const [recordingStatus, setRecordingStatus] = useLocalStorageState<RecordingStatus>(
    'recordingStatus',
    {
      isRecording: false,
      isReuploading: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
      recordingPaused: false,
      readyToRecord: true,
      readyToSubmit: false,
    },
  );

  const webRecordingInstance = useRef<WebRecordingService | undefined>(undefined);

  const isRecordingEnding = useRef<boolean>(false);
  const shouldBeCleanedUp = useRef<boolean>(false);

  const [lowDataMode, setLowDataMode] = useState(false);

  const [chunkLength, setChunkLength] = useState(RECORDING_CHUNK_LENGTH_SECONDS);

  const [noteInformation, setNoteInformation] = useLocalStorageState<NoteInformation>(
    'noteInformation',
    {},
  );

  const [useSharedMedia, setUseSharedMedia] = useLocalStorageState<boolean>(
    'useSharedMedia',
    false,
  );

  const [currentTranscriptionSessionId, setCurrentTranscriptionSessionId] =
    useLocalStorageState<string>('transcriptionSessionId', '');

  const [audioErrorMessage, setAudioErrorMessage] = useState<string>('');
  const [noAudioStatus, setNoAudioStatus] = useLocalStorageState<boolean>('noAudioStatus', false);
  const [microphonePermissionGranted, setMicrophonePermissionGranted] = useLocalStorageState<
    boolean | undefined
  >('microphonePermissionGranted', undefined);
  const [availableMicrophones, setAvailableMicrophones] = useLocalStorageState<MediaDeviceInfo[]>(
    'availableMicrophones',
    [],
  );
  const [selectedMicrophone, setSelectedMicrophoneState] = useLocalStorageState<
    MediaDeviceInfo | undefined
  >('selectedMicrophone', undefined);

  const elapsedTime = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Store actual recording start time for accurate timestamps
  const recordingStartTime = useRef<number | null>(null);

  const currentRecordingSession = useRef<RecordingSession | undefined>(undefined);

  // Ref to store transcription session ID as a fallback for stale closure issues.
  // IMPORTANT: This exists because requestRemoteStop uses useCallback with empty deps,
  // which can cause it to call performStopRecording with a stale closure that has
  // an empty currentTranscriptionSessionId. This is especially problematic on Safari
  // where localStorage may also be unreliable. The ref provides a stable reference
  // that doesn't suffer from stale closure issues.
  const transcriptionSessionIdRef = useRef<string>('');

  // Ref to always hold the latest performStopRecording function.
  // requestRemoteStop uses useCallback([]) which creates a stale closure —
  // calling through this ref ensures we always invoke the current version.
  const performStopRecordingRef = useRef<() => Promise<boolean>>(async () => false);
  const performPauseRecordingRef = useRef<() => boolean>(() => false);
  const performResumeRecordingRef = useRef<() => Promise<boolean>>(async () => false);

  const isLoadedInChromeExtension = useRef(false);

  // Cleanup function for shared media stream mixing (telehealth/headphones mode)
  const streamCleanupRef = useRef<(() => void) | null>(null);
  // Mixer result for mid-recording device switching (Phase 3b)
  const mixerResultRef = useRef<AudioMixerResult | null>(null);

  // Telehealth refs (Phase 3c)
  const dailyCallServiceRef = useRef<DailyCallService | null>(null);
  const deviceBridgeRef = useRef<DeviceBridgeService | null>(null);
  const telehealthConfigRef = useRef<{ roomUrl: string; token: string } | null>(null);
  const dailyEventCleanupRef = useRef<(() => void) | null>(null);

  // Added state for appointment type
  const [appointmentType, setAppointmentType] = useLocalStorageState<string>(
    'appointmentType',
    'inPerson',
  );

  const [allowRecorderControl, setAllowRecorderControl] = useState<boolean>(true);
  const [remoteStopPending, setRemoteStopPending] = useState<boolean>(false);

  const [isMicTesting, setIsMicTesting] = useState<boolean>(false);

  const [storageInfo, setStorageInfo] = useState<{
    usagePercent: number;
    availableMB: number;
    quotaMB: number;
    minimumRequiredQuotaMB: number;
  } | null>(null);
  const [showStorageCleanupDialog, setShowStorageCleanupDialog] = useState(false);

  const [storagePersisted, setStoragePersisted] = useState<boolean | null>(null);

  // --- AudioInputDeviceService integration (driven by consumer via enableAudioDeviceService) ---
  const [useAudioDeviceService, setUseAudioDeviceService] = useState(false);
  const {
    audioDevice: audioDeviceTracking,
    recording: recordingTracking,
    sessionCompletion: sessionCompletionTracking,
    sessionRecovery: sessionRecoveryTracking,
    recordingUI: recordingUITracking,
    error: errorTracking,
  } = useEventTracking();
  const audioDeviceServiceRef = useRef<AudioInputDeviceService | null>(null);
  const [audioHealthState, setAudioHealthState] = useState<HealthState>('idle');
  const [needsMicPermission, setNeedsMicPermission] = useState(false);
  const enableAudioDeviceService = useCallback((enabled: boolean) => {
    setUseAudioDeviceService(enabled);
  }, []);
  // Refs to avoid stale closures in service event handlers
  const isRecordingRef = useRef(false);
  const useAudioDeviceServiceRef = useRef(useAudioDeviceService);
  const failedDevicesRef = useRef<Set<string>>(new Set());

  // --- StorageService integration (driven by consumer via enableStorageService) ---
  const [useStorageService, setUseStorageService] = useState(false);
  const storageServiceRef = useRef<StorageService | null>(null);
  const useStorageServiceRef = useRef(useStorageService);
  const pendingChunkBlobsRef = useRef<Map<string, Blob>>(new Map());
  const enableStorageService = useCallback((enabled: boolean) => {
    setUseStorageService(enabled);
  }, []);

  // --- UploadService integration (driven by consumer via enableUploadService) ---
  const [useUploadService, setUseUploadService] = useState(false);
  const uploadServiceRef = useRef<UploadService | null>(null);
  const useUploadServiceRef = useRef(useUploadService);
  const enableUploadService = useCallback((enabled: boolean) => {
    setUseUploadService(enabled);
  }, []);
  // When recording stops with V3 active, we set this to the sessionId.
  // The queueDrained handler checks it and triggers finalization.
  const pendingFinalizationRef = useRef<string | null>(null);
  const enqueuedChunkCountRef = useRef(0);

  // --- RecordingService integration (driven by consumer via enableRecordingService) ---
  const [useRecordingService, setUseRecordingService] = useState(false);
  const recordingServiceRef = useRef<RecordingService | null>(null);
  const useRecordingServiceRef = useRef(useRecordingService);
  const enableRecordingService = useCallback((enabled: boolean) => {
    setUseRecordingService(enabled);
  }, []);

  // --- SessionCompletionService integration (ADR-007, driven by consumer via enableSessionCompletionService) ---
  const [useSessionCompletionService, setUseSessionCompletionService] = useState(false);
  const sessionCompletionServiceRef = useRef<SessionCompletionService | null>(null);
  const sessionRecoveryServiceRef = useRef<SessionRecoveryService | null>(null);
  const sessionManifestRef = useRef<SessionManifest | null>(null);
  const useSessionCompletionServiceRef = useRef(useSessionCompletionService);
  const enableSessionCompletionService = useCallback((enabled: boolean) => {
    setUseSessionCompletionService(enabled);
  }, []);

  // Sync recording pipeline feature gate state to Sentry for observability.
  // Allows filtering errors by "streaming" vs "classic" in the Sentry dashboard.
  useEffect(() => {
    // High-level tag for quick filtering.
    // RecordingService gate implies Storage + Upload gates are also on (dependency chain).
    SentryService.setTag('recording_pipeline', useRecordingService ? 'streaming' : 'classic');

    // Individual gate tags for granular debugging (e.g. storage on but recording off)
    SentryService.setTag('gate_audio_device', String(useAudioDeviceService));
    SentryService.setTag('gate_storage', String(useStorageService));
    SentryService.setTag('gate_upload', String(useUploadService));
    SentryService.setTag('gate_recording', String(useRecordingService));
    SentryService.setTag('gate_completion', String(useSessionCompletionService));

    return () => {
      SentryService.setTag('recording_pipeline', '');
      SentryService.setTag('gate_audio_device', '');
      SentryService.setTag('gate_storage', '');
      SentryService.setTag('gate_upload', '');
      SentryService.setTag('gate_recording', '');
      SentryService.setTag('gate_completion', '');
    };
  }, [useAudioDeviceService, useStorageService, useUploadService, useRecordingService, useSessionCompletionService]);

  // --- Exposed service instances for UI hooks (useSyncExternalStore) ---
  const [exposedUploadService, setExposedUploadService] = useState<UploadService | null>(null);
  const [exposedRecordingService, setExposedRecordingService] = useState<RecordingService | null>(null);
  const [exposedStorageService, setExposedStorageService] = useState<StorageService | null>(null);
  const [exposedCompletionService, setExposedCompletionService] = useState<SessionCompletionService | null>(null);
  const [exposedRecoveryService, setExposedRecoveryService] = useState<SessionRecoveryService | null>(null);
  const [exposedAudioDeviceService, setExposedAudioDeviceService] = useState<AudioInputDeviceService | null>(null);

  // --- Pipeline error state (V5 init failures surfaced to users) ---
  const [pipelineError, setPipelineError] = useState<PipelineError | null>(null);
  const pipelineRetryCountRef = useRef(0);

  /**
   * Disable all V5 streaming pipeline gates and surface a classified error to the user.
   * Called from any init failure location instead of silently falling back to V1.
   */
  const disableStreamingPipeline = useCallback(
    (source: PipelineErrorSource, error: unknown) => {
      const classified = classifyPipelineError(
        source,
        error,
        pipelineRetryCountRef.current,
      );

      // Disable all V5 gates
      setUseStorageService(false);
      setUseUploadService(false);
      setUseRecordingService(false);
      setUseSessionCompletionService(false);

      setPipelineError(classified);

      SentryService.addBreadcrumb({
        category: 'pipeline.init',
        message: `Pipeline disabled: ${classified.type} from ${source}`,
        level: 'error',
        data: {
          error_type: classified.type,
          source,
          retry_count: pipelineRetryCountRef.current,
        },
      });

      // Track with Statsig
      errorTracking.pipelineInit({
        error_type: classified.type,
        error_source: source,
        error_message: classified.message,
        error_name: error instanceof Error ? error.name : undefined,
        retry_count: pipelineRetryCountRef.current,
        encounter_id: recordingEncounterIdRef.current ?? undefined,
      });
    },
    [errorTracking],
  );

  /**
   * Retry pipeline initialization after a failure. Re-enables the storage gate
   * which triggers the StorageService init useEffect.
   */
  const retryPipelineInit = useCallback(() => {
    if (pipelineRetryCountRef.current >= 3) return;
    pipelineRetryCountRef.current += 1;
    setPipelineError(null);
    // Re-enable storage gate — the useEffect will re-init StorageService
    setUseStorageService(true);
  }, []);

  // S3 upload error callback
  const [onS3UploadError, setOnS3UploadErrorState] = useState<
    ((error: string) => void) | undefined
  >(undefined);

  // Wrapper to properly set the callback function
  const setOnS3UploadError = useCallback((callback: ((error: string) => void) | undefined) => {
    setOnS3UploadErrorState(() => callback);
  }, []);
  // Ref to avoid stale closure in UploadService event handlers
  const onS3UploadErrorRef = useRef(onS3UploadError);
  useEffect(() => {
    onS3UploadErrorRef.current = onS3UploadError;
  }, [onS3UploadError]);

  // Recording finalized callback - called when recording ends and document should be created
  const onRecordingFinalizedRef = useRef<
    ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>) | undefined
  >(undefined);

  // Wrapper to properly set the callback function
  const setOnRecordingFinalized = useCallback(
    (
      callback:
        | ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>)
        | undefined,
    ) => {
      onRecordingFinalizedRef.current = callback;
    },
    [],
  );

  // Callback invoked synchronously after recording stops and readyToSubmit is set.
  // Used by EncounterViewV2 to create the recording document before any cleanup runs.
  const onRecordingStoppedRef = useRef<
    ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>) | undefined
  >(undefined);

  const setOnRecordingStopped = useCallback(
    (
      callback:
        | ((transcriptionSessionId: string, elapsedTime: number) => Promise<void>)
        | undefined,
    ) => {
      onRecordingStoppedRef.current = callback;
    },
    [],
  );

  // Encounter ID ref — set by EncounterViewV2, read at manifest creation time
  const recordingEncounterIdRef = useRef<string | null>(null);
  const setRecordingEncounterId = useCallback((id: string | null) => {
    recordingEncounterIdRef.current = id;
  }, []);
  const getRecordingEncounterId = useCallback(() => recordingEncounterIdRef.current, []);

  // Telehealth session setter — called by EncounterViewV2 when entering a telehealth encounter
  const setTelehealthSession = useCallback(
    (config: { dailyCallService: DailyCallService; roomUrl: string; token: string } | null) => {
      if (config) {
        dailyCallServiceRef.current = config.dailyCallService;
        telehealthConfigRef.current = { roomUrl: config.roomUrl, token: config.token };
      } else {
        dailyCallServiceRef.current = null;
        telehealthConfigRef.current = null;
      }
    },
    [],
  );

  // Full telehealth teardown — called by Phase 4 UI when navigating away from encounter
  const cleanupTelehealthSession = useCallback(() => {
    // 1. Event subscriptions
    dailyEventCleanupRef.current?.();
    dailyEventCleanupRef.current = null;

    // 2. DeviceBridgeService (stops clone, clears provider)
    deviceBridgeRef.current?.dispose();
    deviceBridgeRef.current = null;

    // 3. DailyCallService leave + destroy
    if (dailyCallServiceRef.current) {
      const service = dailyCallServiceRef.current;
      service.leave().then(() => service.destroy()).catch((err) => {
        SentryService.addBreadcrumb({
          category: 'telehealth.cleanup',
          message: `Daily teardown error: ${err}`,
          level: 'warning',
        });
      });
    }

    // 4. Clear refs and config
    dailyCallServiceRef.current = null;
    telehealthConfigRef.current = null;
    SentryService.setTag('telehealth_active', '');
  }, []);

  const rehydrateStateFromLocalStorage = useCallback(() => {
    console.log('Rehydrating state from localStorage');

    // Rehydrate recordingStatus
    const storedRecordingStatus = localStorage.getItem('recordingStatus');
    if (storedRecordingStatus) {
      try {
        const parsedStatus = JSON.parse(storedRecordingStatus);
        setRecordingStatus(parsedStatus);
      } catch (e) {
        console.error('Error parsing recordingStatus from localStorage', e);
      }
    }

    // Rehydrate noteInformation
    const storedNoteInfo = localStorage.getItem('noteInformation');
    if (storedNoteInfo) {
      try {
        const parsedNoteInfo = JSON.parse(storedNoteInfo);
        setNoteInformation(parsedNoteInfo);
      } catch (e) {
        console.error('Error parsing noteInformation from localStorage', e);
      }
    }

    // Rehydrate useSharedMedia
    const storedUseSharedMedia = localStorage.getItem('useSharedMedia');
    if (storedUseSharedMedia) {
      try {
        const parsedUseSharedMedia = JSON.parse(storedUseSharedMedia);
        setUseSharedMedia(parsedUseSharedMedia);
      } catch (e) {
        console.error('Error parsing useSharedMedia from localStorage', e);
      }
    }

    // Rehydrate currentTranscriptionSessionId
    const storedTranscriptionSessionId = localStorage.getItem('transcriptionSessionId');
    if (storedTranscriptionSessionId) {
      try {
        const parsedSessionId = JSON.parse(storedTranscriptionSessionId);
        setCurrentTranscriptionSessionId(parsedSessionId);
        // Also set the ref for fallback purposes
        transcriptionSessionIdRef.current = parsedSessionId;
      } catch (e) {
        console.error('Error parsing transcriptionSessionId from localStorage', e);
      }
    }

    // Rehydrate noAudioStatus
    const storedNoAudioStatus = localStorage.getItem('noAudioStatus');
    if (storedNoAudioStatus) {
      try {
        const parsedNoAudioStatus = JSON.parse(storedNoAudioStatus);
        setNoAudioStatus(parsedNoAudioStatus);
      } catch (e) {
        console.error('Error parsing noAudioStatus from localStorage', e);
      }
    }

    // Rehydrate microphonePermissionGranted
    const storedMicPermission = localStorage.getItem('microphonePermissionGranted');
    if (storedMicPermission) {
      try {
        const parsedMicPermission = JSON.parse(storedMicPermission);
        setMicrophonePermissionGranted(parsedMicPermission);
      } catch (e) {
        console.error('Error parsing microphonePermissionGranted from localStorage', e);
      }
    }

    // Rehydrate availableMicrophones and selectedMicrophone
    // Skip when audio device service gate is on — the service manages these values
    // and rehydration could overwrite normalized data with stale raw data.
    if (!useAudioDeviceService) {
      const storedMics = localStorage.getItem('availableMicrophones');
      if (storedMics) {
        try {
          const parsedMics = JSON.parse(storedMics);
          setAvailableMicrophones(parsedMics);
        } catch (e) {
          console.error('Error parsing availableMicrophones from localStorage', e);
        }
      }

      const storedSelectedMic = localStorage.getItem('selectedMicrophone');
      if (storedSelectedMic) {
        try {
          const parsedSelectedMic = JSON.parse(storedSelectedMic);
          setSelectedMicrophoneState(parsedSelectedMic);
        } catch (e) {
          console.error('Error parsing selectedMicrophone from localStorage', e);
        }
      }
    }

    // Rehydrate appointmentType
    const storedAppointmentType = localStorage.getItem('appointmentType');
    if (storedAppointmentType) {
      try {
        const parsedAppointmentType = JSON.parse(storedAppointmentType);
        setAppointmentType(parsedAppointmentType);
      } catch (e) {
        console.error('Error parsing appointmentType from localStorage', e);
      }
    }

    // Rehydrate elapsedTime
    const storedElapsedTime = localStorage.getItem('elapsedTime');
    if (storedElapsedTime) {
      try {
        elapsedTime.current = parseInt(storedElapsedTime, 10);
      } catch (e) {
        console.error('Error parsing elapsedTime from localStorage', e);
      }
    }

    // Attempt to recover recording session
    attemptRecoverRecordingSession();

    console.log('State rehydration from localStorage complete');
  }, [
    useAudioDeviceService,
    setRecordingStatus,
    setNoteInformation,
    setUseSharedMedia,
    setCurrentTranscriptionSessionId,
    setNoAudioStatus,
    setMicrophonePermissionGranted,
    setAvailableMicrophones,
    setSelectedMicrophoneState,
    setAppointmentType,
  ]);

  const sendMessageToExtension = (message: any) => {
    if (loadedInChromeExtension) {
      console.log('Sending message to extension...');
      window.parent.postMessage(message, '*');
    }
  };

  // Start the recording timer
  const startTimer = () => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      elapsedTime.current = elapsedTime.current + 1;
    }, 1000);
  };

  // Stop the recording timer
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Reset the recording timer
  const resetTimer = () => {
    stopTimer();
    elapsedTime.current = 0;
    recordingStartTime.current = null;
    localStorage.setItem('elapsedTime', '0');
    setAudioErrorMessage('');
  };

  useEffect(() => {
    sendMessageToExtension({
      type: 'audioErrorMessage',
      data: audioErrorMessage,
    });
  }, [audioErrorMessage]);
  const enterLowDataMode = () => {
    setLowDataMode(true);

    console.log('Entering low data mode.');
    currentRecordingSession.current?.addLog('ENTERING LOW DATA MODE');
    SentryService.logEvent('Entering low data mode.', {
      level: 'info',
      extra: { transcriptionSessionId: currentTranscriptionSessionId },
    });
    webRecordingInstance.current?.updateRecordingOption(
      'chunkLength',
      LOW_DATA_CHUNK_LENGTH_SECTIONS * 1000,
    );
    setChunkLength(LOW_DATA_CHUNK_LENGTH_SECTIONS);
  };

  // Internal function to perform the actual pause recording logic
  const performPauseRecording = (): boolean => {
    if (useRecordingServiceRef.current && recordingServiceRef.current) {
      // V4 path: RecordingService flushes accumulated data and pauses MediaRecorder
      recordingServiceRef.current.pause();
    } else {
      if (!webRecordingInstance.current) {
        webRecordingInstance.current = new WebRecordingService();
      }
      webRecordingInstance.current?.pauseRecording();
    }

    setRecordingStatus(prev => ({
      ...prev,
      recordingPaused: true,
    }));

    // Pause audio history updates
    setPaused(true);
    return true;
  };

  // Keep ref in sync so requestRemotePause (stale useCallback) always calls the latest version
  performPauseRecordingRef.current = performPauseRecording;

  // Pause the recording
  const pauseRecording = () => {
    // Only allow the owner tab to pause the recording
    if (!allowRecorderControl) {
      enqueueSnackbar('Only the tab that started the recording can control it', {
        variant: 'warning',
      });
      return;
    }

    performPauseRecording();
  };

  // Internal function to perform the actual resume recording logic
  const performResumeRecording = async (): Promise<boolean> => {
    try {
      if (useRecordingServiceRef.current && recordingServiceRef.current) {
        // V4 path: RecordingService resumes MediaRecorder and restarts timers
        await recordingServiceRef.current.resume();
      } else {
        if (!webRecordingInstance.current) {
          webRecordingInstance.current = new WebRecordingService();
        }

        // if the recorder isn't set up yet (e.g. on reload)
        // then create a new one
        if (!webRecordingInstance.current?.recorder) {
          const mediaRecorderCreated = await createMediaRecorder();
          if (!mediaRecorderCreated) {
            console.error('Failed to create media recorder.');
            return false;
          }
        } else {
          webRecordingInstance.current.resumeRecording();
        }
      }

      setRecordingStatus(prev => ({
        ...prev,
        recordingPaused: false,
      }));

      // Resume audio history updates
      setPaused(false);
      return true;
    } catch (error) {
      console.error('Error resuming recording:', error);
      SentryService.logEvent('Error resuming recording.', {
        level: 'error',
        extra: { error },
      });
      setAudioErrorMessage('Failed to resume recording. Try again.');
      enqueueSnackbar('Failed to resume recording. Try again.', {
        variant: 'error',
      });
      setTimeout(() => setAudioErrorMessage(''), 3000);
      return false;
    }
  };

  // Keep ref in sync so requestRemoteResume (stale useCallback) always calls the latest version
  performResumeRecordingRef.current = performResumeRecording;

  // Resume the recording
  const resumeRecording = async (): Promise<boolean> => {
    // Only allow the owner tab to resume the recording
    if (!allowRecorderControl) {
      enqueueSnackbar('Only the tab that started the recording can control it', {
        variant: 'warning',
      });
      return false;
    }

    return performResumeRecording();
  };

  const setShouldBeCleanedUp = (value: boolean) => {
    shouldBeCleanedUp.current = value;
  };

  const initiateReupload = async (): Promise<boolean> => {
    if (!currentRecordingSession.current || !currentTranscriptionSessionId) {
      return false;
    }

    if (useUploadServiceRef.current && uploadServiceRef.current) {
      // V3: retry via UploadService dead letter queue + re-enqueue failed chunks
      try {
        const deadLetterEntries = uploadServiceRef.current.getSnapshot().deadLetterQueue;
        for (const entry of deadLetterEntries) {
          await uploadServiceRef.current.retryDeadLetter(entry.sessionId, entry.chunkIndex);
        }

        // Also re-enqueue any RecordingSession failed chunks not in dead letter
        const failedChunks = currentRecordingSession.current.getFailedChunks();
        const deadLetterIndices = new Set(deadLetterEntries.map(e => e.chunkIndex));
        for (const chunk of failedChunks) {
          if (chunk.position != null && !deadLetterIndices.has(chunk.position)) {
            uploadServiceRef.current.enqueue({
              sessionId: currentTranscriptionSessionId,
              chunkIndex: chunk.position,
              chunkId: chunk.id,
              contentType: chunk.chunk_data?.chunk_type || 'audio/webm',
              byteSize: chunk.chunk_data?.chunk_size || 0,
            });
          }
        }

        // Wait for queueDrained to finalize
        pendingFinalizationRef.current = currentTranscriptionSessionId;
        setRecordingStatus(prev => ({ ...prev, isReuploading: true }));
        return true;
      } catch (e) {
        console.error("Couldn't reupload chunks (V3)");
        return false;
      }
    }

    try {
      const chunksToBeUploaded = currentRecordingSession.current.getFailedChunks();

      await reuploadChunks(chunksToBeUploaded, currentTranscriptionSessionId);

      return true;
    } catch (e) {
      console.error("Couldn't reupload chunks");
      return false;
    }
  };

  // --- AudioInputDeviceService ref sync effects ---
  useEffect(() => {
    isRecordingRef.current = recordingStatus.isRecording;
  }, [recordingStatus.isRecording]);
  useEffect(() => {
    useAudioDeviceServiceRef.current = useAudioDeviceService;
  }, [useAudioDeviceService]);
  useEffect(() => {
    useStorageServiceRef.current = useStorageService;
  }, [useStorageService]);
  useEffect(() => {
    useUploadServiceRef.current = useUploadService;
  }, [useUploadService]);
  useEffect(() => {
    useRecordingServiceRef.current = useRecordingService;
  }, [useRecordingService]);
  useEffect(() => {
    useSessionCompletionServiceRef.current = useSessionCompletionService;
  }, [useSessionCompletionService]);

  // Initialize/dispose StorageService when gate enabled
  useEffect(() => {
    if (!useStorageService) return;

    let disposed = false;
    const service = new StorageService();
    storageServiceRef.current = service;
    setExposedStorageService(service);

    service
      .initialize()
      .then(() => {
        if (disposed) {
          service.dispose();
          return;
        }
        // Reset retry count on successful init
        pipelineRetryCountRef.current = 0;
        SentryService.addBreadcrumb({
          category: 'storage.service',
          message: `StorageService ready, backend: ${service.getSnapshot().backendType}`,
          level: 'info',
        });
      })
      .catch((error: unknown) => {
        if (disposed) return;
        SentryService.captureException(
          error instanceof Error ? error : new Error(String(error)),
        );
        disableStreamingPipeline('storage_init', error);
      });

    service.on('quotaWarning', ({ usage, quota }) => {
      SentryService.addBreadcrumb({
        category: 'storage.quota',
        message: `Storage quota warning: ${Math.round(usage / 1024 / 1024)}MB / ${Math.round(quota / 1024 / 1024)}MB`,
        level: 'warning',
      });
    });

    return () => {
      disposed = true;
      service.dispose();
      storageServiceRef.current = null;
      setExposedStorageService(null);
      pendingChunkBlobsRef.current.clear();
    };
  }, [useStorageService]);

  // Dispose AudioInputDeviceService on unmount
  useEffect(() => {
    return () => {
      if (audioDeviceServiceRef.current) {
        audioDeviceServiceRef.current.dispose();
        audioDeviceServiceRef.current = null;
        setExposedAudioDeviceService(null);
      }
    };
  }, []);

  /** Lazily create AudioInputDeviceService and wire event listeners */
  const getOrCreateAudioService = useCallback((): AudioInputDeviceService => {
    if (!audioDeviceServiceRef.current) {
      const service = new AudioInputDeviceService();
      audioDeviceServiceRef.current = service;
      setExposedAudioDeviceService(service);

      service.on('healthChanged', ({ state, prev }: { state: HealthState; prev: HealthState }) => {
        setAudioHealthState(state);
        // Clear failed-device memory when a device proves healthy
        if (state === 'healthy') {
          failedDevicesRef.current.clear();
        }
        audioDeviceTracking.healthChanged({
          from_state: prev,
          to_state: state,
          during_recording: isRecordingRef.current,
        });
      });

      // perfSample events are NOT forwarded to Statsig — they fire every 250ms
      // and would generate ~7,200 events per 30-min session. The aggregated
      // audio_performance_summary (1 event per session) provides the same data.

      service.on('performanceSummary', summary => {
        audioDeviceTracking.performanceSummary({
          ...summary,
        });
      });

      service.on('devicesChanged', (diff: DeviceDiff) => {
        setAvailableMicrophones(service.getDevices().map(normalizedToMediaDeviceInfo));
        audioDeviceTracking.deviceChanged({
          added_count: diff.added.length,
          removed_count: diff.removed.length,
          changed_count: diff.changed.length,
          during_recording: isRecordingRef.current,
        });
      });

      service.on('deviceLost', (device: NormalizedDevice) => {
        audioDeviceTracking.deviceLost({
          device_transport: device.transport,
          during_recording: isRecordingRef.current,
        });
      });

      service.on('permissionChanged', (result: AudioPermissionResult) => {
        audioDeviceTracking.permissionResult({
          granted: result.granted,
          error_type: result.error,
        });
      });

      // Bridge health monitor level data → AudioDataContext for VU meter visualization.
      // Only feeds data when NOT recording (during recording, WebRecordingService's
      // onAudioImpulse takes priority since it reflects the actual recording mix).
      service.setOnLevelChanged((level: number) => {
        if (!isRecordingRef.current) {
          setAudioData(level);
        }
      });
    }
    return audioDeviceServiceRef.current;
  }, [setAvailableMicrophones, setAudioData]);

  // "Waiting room" initialization: silently check if mic permission is already cached in the
  // browser via permissions.query(). If cached, enumerate devices and auto-acquire a stream.
  // If not, set needsMicPermission so the UI shows a permission overlay with a user-gesture button
  // (browsers require a user gesture for getUserMedia to trigger the permission prompt).
  useEffect(() => {
    if (!useAudioDeviceService) return;

    const service = getOrCreateAudioService();
    if (service.isInitialized()) return;

    let cancelled = false;

    (async () => {
      try {
        // Silent check — uses permissions.query(), no browser prompt, works without user gesture
        const permission = await service.initializeSilent();
        if (cancelled) return;

        if (!permission.granted) {
          // Permission not cached — show overlay so user can click to grant
          setNeedsMicPermission(true);
          return;
        }

        // Silent check succeeded — permission was cached in browser
        setNeedsMicPermission(false);
        setMicrophonePermissionGranted(true);

        // Sync normalized device list
        const devices = service.getDevices();
        const mediaDevices = devices.map(normalizedToMediaDeviceInfo);
        setAvailableMicrophones(mediaDevices);

        // Restore preference and determine target device
        let targetDevice: NormalizedDevice | undefined;
        const pref = service.getPreferenceStore().load();
        if (pref && devices.length > 0) {
          const match = service.getPreferenceStore().findBestMatch(pref, devices);
          if (match) {
            setSelectedMicrophoneState(normalizedToMediaDeviceInfo(match));
            localStorage.setItem('selectedMicrophoneId', match.browserDeviceId);
            targetDevice = match;
          }
        }

        if (cancelled) return;

        // Auto-acquire stream — mic is now live, health monitoring starts,
        // and level data flows to AudioDataContext for the VU meter.
        const acquireResult = await service.acquire(targetDevice?.internalId);

        // Track auto-acquisition for analytics
        audioDeviceTracking.micAutoAcquired?.({
          microphone_id: acquireResult.device.browserDeviceId,
          device_transport: acquireResult.device.transport,
        });
      } catch (error) {
        // On any error, show the overlay as a fallback so user can manually trigger permission
        if (!cancelled) setNeedsMicPermission(true);
        console.warn('Waiting room audio init failed (non-fatal)', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [useAudioDeviceService, getOrCreateAudioService, setAvailableMicrophones, setMicrophonePermissionGranted]);

  // Auto-select best available mic when the selected device disappears (e.g., Bluetooth disconnect)
  useEffect(() => {
    if (!useAudioDeviceService || !selectedMicrophone || availableMicrophones.length === 0) return;

    const stillAvailable = availableMicrophones.some(
      m => m.deviceId === selectedMicrophone.deviceId,
    );

    if (!stillAvailable) {
      const best = availableMicrophones[0]; // Already sorted by priority (builtin > usb > bluetooth)
      setSelectedMicrophoneState(best);
      localStorage.setItem('selectedMicrophoneId', best.deviceId);
      enqueueSnackbar(
        `${selectedMicrophone.label || 'Microphone'} disconnected. Switched to ${best.label || 'default microphone'}.`,
        { variant: 'warning' },
      );

      if (isRecordingRef.current && useRecordingServiceRef.current && mixerResultRef.current) {
        // Mid-recording: hot-swap the mixer source so the new mic feeds into the recording
        const service = audioDeviceServiceRef.current;
        if (service?.isInitialized()) {
          const device = service.getDevices().find(d => d.browserDeviceId === best.deviceId);
          if (device) {
            handleMidRecordingDeviceSwitch(device.internalId).catch(err => {
              SentryService.addBreadcrumb({
                category: 'recording.context',
                message: `Auto-disconnect mid-recording device switch failed: ${err}`,
                level: 'error',
              });
            });
          }
        }
      } else {
        // Not recording: re-acquire stream on the fallback device so health monitoring restarts
        // (triggers 'initializing' → spinner in AudioLevelMeter)
        const service = audioDeviceServiceRef.current;
        if (service?.isInitialized()) {
          const device = service.getDevices().find(d => d.browserDeviceId === best.deviceId);
          service.acquire(device?.internalId).catch(err => {
            console.warn('Failed to re-acquire stream after device switch', err);
          });
        }
      }
    }
  }, [useAudioDeviceService, selectedMicrophone, availableMicrophones, enqueueSnackbar, setSelectedMicrophoneState]);

  // Auto-fallback: when health reaches 'failed' and not recording,
  // try the next available device that hasn't already failed
  useEffect(() => {
    if (!useAudioDeviceService) return;
    if (audioHealthState !== 'failed') return;
    if (recordingStatus.isRecording) return;

    const service = audioDeviceServiceRef.current;
    if (!service?.isInitialized()) return;

    // Mark the current device as failed
    const activeStream = service.getActiveStream();
    if (activeStream) {
      failedDevicesRef.current.add(activeStream.device.internalId);
    }

    // Find next untried device
    const devices = service.getDevices();
    const candidate = devices.find(d => !failedDevicesRef.current.has(d.internalId));

    if (!candidate) return; // All devices tried — user must intervene

    // Switch UI + persist
    const candidateMedia = normalizedToMediaDeviceInfo(candidate);
    setSelectedMicrophoneState(candidateMedia);
    localStorage.setItem('selectedMicrophoneId', candidateMedia.deviceId);
    enqueueSnackbar(
      `${selectedMicrophone?.label || 'Microphone'} isn't working. Trying ${candidate.displayLabel || 'next microphone'}...`,
      { variant: 'warning' },
    );

    // Track event
    audioDeviceTracking.autoFallback({
      failed_device_label: activeStream?.device.displayLabel,
      candidate_device_label: candidate.displayLabel,
      devices_tried_count: failedDevicesRef.current.size,
    });

    // Acquire triggers healthMonitor.start() → 'initializing' → spinner
    service.acquire(candidate.internalId).catch(err => {
      console.warn('Auto-fallback acquire failed', err);
    });
  }, [
    useAudioDeviceService,
    audioHealthState,
    recordingStatus.isRecording,
    selectedMicrophone,
    enqueueSnackbar,
    setSelectedMicrophoneState,
  ]);

  const checkMicrophonePermission = async (): Promise<boolean> => {
    // --- Gated: delegate to AudioInputDeviceService ---
    if (useAudioDeviceService) {
      try {
        const service = getOrCreateAudioService();

        if (!service.isInitialized()) {
          const permission = await service.initialize();
          if (!permission.granted) {
            setMicrophonePermissionGranted(false);
            // When the overlay is active, let it handle the error display
            if (!needsMicPermission) {
              const errorMessage =
                permission.error === 'not_found'
                  ? 'No microphone found. Please connect a microphone to use the recorder.'
                  : "Microphone access is denied. You'll need to enable it to use the recorder.";
              setAudioErrorMessage(errorMessage);
              setTimeout(() => setAudioErrorMessage(''), 3000);
              enqueueSnackbar(errorMessage, { variant: 'error' });
            }
            return false;
          }
        }

        setMicrophonePermissionGranted(true);
        setNeedsMicPermission(false);

        // Sync device list to context state
        const devices = service.getDevices();
        const mediaDevices = devices.map(normalizedToMediaDeviceInfo);
        setAvailableMicrophones(mediaDevices);

        // Restore selected mic from service preference store
        const pref = service.getPreferenceStore().load();
        if (pref && devices.length > 0) {
          const match = service.getPreferenceStore().findBestMatch(pref, devices);
          if (match) {
            setSelectedMicrophoneState(normalizedToMediaDeviceInfo(match));
            localStorage.setItem('selectedMicrophoneId', match.browserDeviceId);
          } else if (!selectedMicrophone && mediaDevices.length > 0) {
            setSelectedMicrophoneState(mediaDevices[0]);
            localStorage.setItem('selectedMicrophoneId', mediaDevices[0].deviceId);
          }
        } else if (!selectedMicrophone && mediaDevices.length > 0) {
          setSelectedMicrophoneState(mediaDevices[0]);
          localStorage.setItem('selectedMicrophoneId', mediaDevices[0].deviceId);
        }

        audioDeviceTracking.initialized({
          device_count: devices.length,
          permission_granted: true,
          selected_transport: devices.find(
            d => d.browserDeviceId === selectedMicrophone?.deviceId,
          )?.transport,
        });

        // Pre-acquire stream for health monitoring (before recording starts)
        try {
          await service.acquire();
          // healthChanged events flow through getOrCreateAudioService listener → setAudioHealthState
        } catch (acquireError) {
          // Non-fatal: recording can still acquire later
          console.warn('Pre-acquisition for health monitoring failed', acquireError);
          SentryService.addBreadcrumb({
            category: 'audio.service',
            message: 'Pre-acquire for health monitoring failed (non-fatal)',
            level: 'warning',
            data: {
              error:
                acquireError instanceof Error ? acquireError.message : String(acquireError),
            },
          });
        }

        return true;
      } catch (error: any) {
        console.error('AudioInputDeviceService initialization error', error);
        SentryService.logEvent('AudioInputDeviceService initialization error', {
          level: 'error',
          extra: { error },
        });
        setMicrophonePermissionGranted(false);
        return false;
      }
    }

    // --- Legacy: direct getUserMedia ---
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('Microphone access granted');
      setMicrophonePermissionGranted(true);

      // Stop the tracks immediately to release the microphone
      stream.getTracks().forEach(t => t.stop());

      // After permission is granted, enumerate devices
      await getAvailableMicrophones();

      return true;
    } catch (error: any) {
      console.error('Error requesting microphone permission', error);
      SentryService.logEvent('Error requesting microphone permission.', {
        level: 'error',
        extra: { error },
      });

      if (error.name === 'NotAllowedError') {
        console.log('Permission explicitly denied');
        setMicrophonePermissionGranted(false);
        const errorMessage =
          "Microphone access is denied. You'll need to enable it to use the recorder.";
        setAudioErrorMessage(errorMessage);
        setTimeout(() => setAudioErrorMessage(''), 3000);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } else if (error.name === 'NotFoundError') {
        console.log('No microphone found');
        setMicrophonePermissionGranted(false);
        const errorMessage =
          'No microphone found. Please connect a microphone to use the recorder.';
        setAudioErrorMessage(errorMessage);
        setTimeout(() => setAudioErrorMessage(''), 3000);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } else {
        console.log('Other error', error.name);
        const errorMessage = `Error accessing microphone: ${error.message}`;
        setAudioErrorMessage(errorMessage);
        setTimeout(() => setAudioErrorMessage(''), 3000);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }

      return false;
    }
  };

  const getAvailableMicrophones = async () => {
    // --- Gated: read from AudioInputDeviceService ---
    if (useAudioDeviceService && audioDeviceServiceRef.current?.isInitialized()) {
      const devices = audioDeviceServiceRef.current.getDevices();
      const mediaDevices = devices.map(normalizedToMediaDeviceInfo);
      setAvailableMicrophones(mediaDevices);

      const storedMicrophoneId = localStorage.getItem('selectedMicrophoneId');
      if (storedMicrophoneId) {
        const stored = mediaDevices.find(m => m.deviceId === storedMicrophoneId);
        if (stored) {
          setSelectedMicrophone(stored);
          return;
        }
      }

      if (mediaDevices.length > 0 && !selectedMicrophone) {
        setSelectedMicrophone(mediaDevices[0]);
      }
      return;
    }

    // --- Legacy: direct enumerateDevices ---
    try {
      // Only request permission if we don't have it yet
      if (microphonePermissionGranted === undefined) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        setMicrophonePermissionGranted(true);
        // Stop the tracks immediately to release the microphone
        stream.getTracks().forEach(t => t.stop());
      }

      // Enumerate devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(device => device.kind === 'audioinput');

      // Log available microphones for debugging
      console.log('Available microphones:', mics);
      setAvailableMicrophones(mics);

      // Check if a previously selected microphone is stored in localStorage
      const storedMicrophoneId = localStorage.getItem('selectedMicrophoneId');
      if (storedMicrophoneId) {
        const storedMicrophone = mics.find(mic => mic.deviceId === storedMicrophoneId);

        if (storedMicrophone) {
          console.log('Setting previously selected microphone');
          setSelectedMicrophone(storedMicrophone);
          return;
        }
      }

      // If no stored microphone or stored microphone not available, set the first microphone as default
      if (mics.length > 0 && !selectedMicrophone) {
        console.log('Setting default selected microphone');
        setSelectedMicrophone(mics[0]);
      }
    } catch (error) {
      console.error('Error getting available microphones:', error);
      SentryService.logEvent('Error getting available microphones.', {
        level: 'error',
        extra: { error },
      });
      throw error;
    }
  };

  // Set the selected microphone and store it in localStorage
  const setSelectedMicrophone = (mic: MediaDeviceInfo) => {
    failedDevicesRef.current.clear();
    setSelectedMicrophoneState(mic);
    localStorage.setItem('selectedMicrophoneId', mic.deviceId);

    // Sync to service preference store when gated
    if (useAudioDeviceService && audioDeviceServiceRef.current?.isInitialized()) {
      const normalizedDevice = audioDeviceServiceRef.current
        .getDevices()
        .find(d => d.browserDeviceId === mic.deviceId);
      if (normalizedDevice) {
        audioDeviceServiceRef.current.getPreferenceStore().save(normalizedDevice);

        if (isRecordingRef.current && useRecordingServiceRef.current && mixerResultRef.current) {
          // V5 mid-recording device switch
          handleMidRecordingDeviceSwitch(normalizedDevice.internalId).catch(err => {
            SentryService.addBreadcrumb({
              category: 'recording.context',
              message: `Mid-recording device switch failed: ${err}`,
              level: 'error',
            });
          });
        } else if (!isRecordingRef.current) {
          // Re-acquire for health monitoring on the new device (only when not recording, and only if different)
          const activeStream = audioDeviceServiceRef.current.getActiveStream();
          if (
            activeStream &&
            activeStream.device.internalId !== normalizedDevice.internalId
          ) {
            audioDeviceServiceRef.current
              .acquire(normalizedDevice.internalId)
              .then((result) => {
                // If telehealth call is active (but not recording), update Daily's mic source
                if (dailyCallServiceRef.current?.getCallObject()) {
                  const newTrack = result.stream.getAudioTracks()[0];
                  if (newTrack) {
                    dailyCallServiceRef.current.setAudioDevice(newTrack.clone()).catch(err => {
                      SentryService.addBreadcrumb({
                        category: 'telehealth.device',
                        message: `Daily mic update failed (non-recording): ${err}`,
                        level: 'warning',
                      });
                    });
                  }
                }
              })
              .catch(acquireError => {
                console.warn('Re-acquire on mic change failed (non-fatal)', acquireError);
                SentryService.addBreadcrumb({
                  category: 'audio.service',
                  message: 'Re-acquire on mic change failed',
                  level: 'warning',
                });
              });
          }
        }
      }
    }
  };

  const releaseMicrophone = () => {
    if (webRecordingInstance.current) {
      webRecordingInstance.current.stopMediaStreamRecording();
    }
  };

  /** Release the audio device service stream (e.g., on page navigation). No-op during recording. */
  const releaseAudioStream = useCallback(() => {
    if (isRecordingRef.current) return;
    if (audioDeviceServiceRef.current?.getActiveStream()) {
      audioDeviceServiceRef.current.release();
      setAudioHealthState('idle');
    }
  }, []);

  /** Mid-recording device switch: hot-swap the mixer's mic source without interrupting MediaRecorder. */
  async function handleMidRecordingDeviceSwitch(newDeviceInternalId: string) {
    // 1. Flush current chunk for clean boundary
    recordingServiceRef.current?.flush();

    // 2. Release old mic, acquire new one
    //    release() calls track.stop() — mixer's old source node produces silence
    //    acquire() calls getUserMedia — ~5-50ms gap
    audioDeviceServiceRef.current!.release();
    const acquireResult = await audioDeviceServiceRef.current!.acquire(newDeviceInternalId);

    // 3. Hot-swap the mixer's mic source (MediaRecorder unaffected)
    mixerResultRef.current!.swapMicSource(acquireResult.stream);

    // 4. Update RecordingService state
    const newDeviceId = acquireResult.stream.getAudioTracks()[0]?.getSettings()?.deviceId;
    recordingServiceRef.current?.updateDeviceId(newDeviceId ?? newDeviceInternalId);

    // 5. Re-activate BackgroundThrottleGuard with new raw mic stream
    //    Old raw stream tracks are dead (release() stopped them), guard needs fresh tracks
    recordingServiceRef.current?.updateRawMicStream(acquireResult.stream);

    // Phase 3c: propagate to Daily's mic clone (if telehealth active)
    if (deviceBridgeRef.current) {
      const newMicTrack = acquireResult.stream.getAudioTracks()[0];
      deviceBridgeRef.current.handleDeviceSwitch(newMicTrack).catch((err) => {
        SentryService.addBreadcrumb({
          category: 'telehealth.bridge',
          message: `DeviceBridge switch failed: ${err}`,
          level: 'error',
        });
      });
    }
  }

  // Start mic test mode - only monitors audio levels without recording
  const startMicTest = async (): Promise<boolean> => {
    try {
      // Check microphone permission first
      const permissionGranted = await checkMicrophonePermission();
      if (!permissionGranted) {
        return false;
      }

      // Initialize web recording service if needed
      if (!webRecordingInstance.current) {
        webRecordingInstance.current = new WebRecordingService();
      }

      setIsMicTesting(true);

      // Start mic test with audio level callback
      const success = await webRecordingInstance.current.startMicTest(
        selectedMicrophone?.deviceId,
        {
          onAudioImpulse: averageLevel => {
            // Scaling that emphasizes 10-100 range, compresses 100-255
            let finalValue;

            if (averageLevel <= 10) {
              // 0-10 maps to 0-10 (linear)
              finalValue = averageLevel;
            } else if (averageLevel <= 100) {
              // 10-100 maps to 10-90 (expanded range)
              const percent = (averageLevel - 10) / 90;
              finalValue = 10 + percent * 80;
            } else {
              // 100-255 maps to 90-100 (compressed range)
              const percent = (averageLevel - 100) / 155;
              finalValue = 90 + percent * 10;
            }

            setAudioData(finalValue);
          },
        },
      );

      if (!success) {
        setIsMicTesting(false);
      }

      return success;
    } catch (error) {
      console.error('Error starting mic test:', error);
      setIsMicTesting(false);
      return false;
    }
  };

  // Stop mic test mode
  const stopMicTest = () => {
    if (webRecordingInstance.current && isMicTesting) {
      webRecordingInstance.current.stopMicTest();
      setIsMicTesting(false);
      // Don't reset audio data - keep it to show the checkmark
    }
  };

  const createMediaRecorder = async (
    transcriptionSessionId?: string,
    preAcquiredStream?: MediaStream,
  ) => {
    console.log('useSharedMedia', useSharedMedia, isLoadedInChromeExtension.current);
    currentRecordingSession?.current?.addLog('CreateMediaRecorder', {
      useSharedMedia,
      selectedMicrophoneId: selectedMicrophone?.deviceId,
      selectedMicrophoneLabel: selectedMicrophone?.label,
      chunkLength,
      lowDataMode,
      platform,
      browserInfo: navigator?.userAgent,
      screenWidth: window?.screen?.width,
      screenHeight: window?.screen?.height,
      devicePixelRatio: window?.devicePixelRatio,
      transcriptionSessionId,
    });
    try {
      const mediaStreamResponse = await webRecordingInstance.current?.startMediaRecorder(
        {
          getSharedMedia: useSharedMedia,
          getTabMedia: useSharedMedia && isLoadedInChromeExtension.current,
          selectedMicrophoneId: selectedMicrophone?.deviceId,
          chunkLength: chunkLength * 1000,
          preAcquiredStream,
        },
        {
          recorder: {
            // V2 storage path: hold blob in memory for handleChunk to persist via StorageService
            onProcessChunk:
              useStorageServiceRef.current && storageServiceRef.current
                ? async (blob: Blob, chunkId: string) => {
                    pendingChunkBlobsRef.current.set(chunkId, blob);
                  }
                : undefined,
            onChunkReady: async (chunkId: string) => {
              console.log(`${chunkId} chunk ready to be pulled from indexdb and uploaded`);

              // CRITICAL FIX: Use the recording session's current transcription ID
              // This ensures chunks go to the right session even if created after a failure
              const sessionId = currentRecordingSession.current?.transcription_session_id;

              if (!sessionId) {
                console.error('No transcription session associated with recording session', {
                  chunkId,
                  recordingSessionId: currentRecordingSession.current?.id,
                  closureSessionId: transcriptionSessionId,
                  contextSessionId: currentTranscriptionSessionId,
                });
                await handleChunkError(
                  chunkId,
                  new Error('No transcription session for recording'),
                );
                return;
              }

              // Log if session ID has changed since media recorder creation
              if (transcriptionSessionId && transcriptionSessionId !== sessionId) {
                console.warn('Session ID changed since media recorder creation', {
                  original: transcriptionSessionId,
                  current: sessionId,
                  chunkId,
                });

                SentryService.addBreadcrumb({
                  category: 'session.change',
                  message: 'Transcription session changed during recording',
                  level: 'warning',
                  data: {
                    originalSessionId: transcriptionSessionId,
                    currentSessionId: sessionId,
                    chunkId,
                  },
                });
              }

              await handleChunk(chunkId, sessionId);
            },
            onChunkError: async (chunkId: string, error: unknown) => {
              await handleChunkError(chunkId, error);
            },
          },
          audioStream: {
            onAudioImpulse: averageLevel => {
              // Scaling that emphasizes 10-100 range, compresses 100-255
              let finalValue;

              if (averageLevel <= 10) {
                // 0-10 maps to 0-10 (linear)
                finalValue = averageLevel;
              } else if (averageLevel <= 100) {
                // 10-100 maps to 10-90 (expanded range)
                const percent = (averageLevel - 10) / 90;
                finalValue = 10 + percent * 80;
              } else {
                // 100-255 maps to 90-100 (compressed range)
                const percent = (averageLevel - 100) / 155;
                finalValue = 90 + percent * 10;
              }

              setAudioData(finalValue);
            },
            onNoAudio: () => {
              // When audio device service is active, AudioHealthAlert handles this
              if (useAudioDeviceServiceRef.current) {
                SentryService.logEvent('No audio detected (suppressed - service active).', {
                  level: 'info',
                  extra: { selectedMicrophone },
                });
                currentRecordingSession.current?.addLog('NoAudioDetected_ServiceActive', {
                  selectedMicrophone,
                });
                recordingTracking.noAudioDetected({
                  session_id: transcriptionSessionIdRef.current ?? undefined,
                  encounter_id: recordingEncounterIdRef.current ?? undefined,
                  recording_pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
                  audio_device_service_active: true,
                  duration_at_detection_seconds: elapsedTime.current,
                });
                return;
              }

              const errorMessage = 'No audio detected. Please check your microphone.';
              setAudioErrorMessage(errorMessage);
              setTimeout(() => setAudioErrorMessage(''), 3000);
              if (!loadedInChromeExtension) {
                enqueueSnackbar(errorMessage, { variant: 'error', preventDuplicate: true });
              }
              SentryService.logEvent('No audio detected.', {
                level: 'warning',
                extra: {
                  selectedMicrophone,
                },
              });
              currentRecordingSession.current?.addLog('NoAudioDetected', {
                selectedMicrophone,
              });
              recordingTracking.noAudioDetected({
                session_id: transcriptionSessionIdRef.current ?? undefined,
                encounter_id: recordingEncounterIdRef.current ?? undefined,
                recording_pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
                audio_device_service_active: false,
                duration_at_detection_seconds: elapsedTime.current,
              });
            },
            onSharedMediaError: () => {
              const errorMessage =
                "No audio from screen capture. Did you have 'Also share tab audio' enabled?";
              setAudioErrorMessage(errorMessage);
              setTimeout(() => setAudioErrorMessage(''), 3000);
              enqueueSnackbar(errorMessage, { variant: 'warning' });
              SentryService.logEvent('No audio from screen capture.', {
                level: 'warning',
                extra: {
                  selectedMicrophone,
                },
              });
              currentRecordingSession.current?.addLog('SharedMediaError', {
                selectedMicrophone,
              });
            },
          },
        },
      );

      if (
        !mediaStreamResponse ||
        !currentRecordingSession.current ||
        !webRecordingInstance.current
      ) {
        throw new Error('Failed to create media recorder');
      }

      const mediaStreamInfo = webRecordingInstance.current.getMediaStreamInfo();
      if (mediaStreamInfo) {
        currentRecordingSession.current?.addMediaStreamInfo({
          streamCreatedAt: new Date().toISOString(),
          ...mediaStreamInfo,
        });
      } else {
        console.warn('Media stream info not available');
        currentRecordingSession.current.addLog('MediaStreamInfoUnavailable', {});
      }

      // Log successful creation of media recorder
      currentRecordingSession.current.addLog('MediaRecorderCreated', {
        success: true,
      });

      return true;
    } catch (error) {
      console.error('Error creating media recorder:', error);
      SentryService.logEvent('Error creating media recorder.', {
        level: 'error',
        extra: { error },
      });
      currentRecordingSession.current?.addLog('MediaRecorderCreationError', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  };

  const reuploadChunks = async (
    chunksToRetry: RecordingChunk[],
    transcriptionSessionId: string,
  ) => {
    setRecordingStatus(prev => ({
      ...prev,
      isReuploading: true,
    }));

    for (const chunk of chunksToRetry) {
      try {
        // Use the chunk's stored session ID or fall back to provided ID
        const targetSessionId = chunk.transcription_session_id || transcriptionSessionId;

        console.log(`Reuploading chunk ${chunk.id} to session ${targetSessionId}`, {
          storedSessionId: chunk.transcription_session_id,
          fallbackSessionId: transcriptionSessionId,
        });

        await uploadChunk(chunk, targetSessionId);
      } catch (e) {
        setRecordingStatus(prev => ({
          ...prev,
          isReuploading: false,
          chunksToBeUploaded: true,
        }));
        const errorMessage = 'Some audio could not be reuploaded. Try again.';
        setAudioErrorMessage(errorMessage);
        setTimeout(() => setAudioErrorMessage(''), 3000);
        enqueueSnackbar(errorMessage, { variant: 'error' });
        console.error('One or more chunks could not be reuploaded.');
        throw new Error('One or more chunks could not be reuploaded.');
      }
    }

    console.log('All chunks have been successfully reuploaded.');
    enqueueSnackbar('All remaining audio has been successfully reuploaded.', {
      variant: 'success',
    });
    setRecordingStatus(prev => ({
      ...prev,
      isReuploading: false,
      chunksToBeUploaded: false,
    }));
    finalizeTranscriptionSession(transcriptionSessionId);
  };

  const uploadChunk = async (chunk: RecordingChunk, transcriptionSessionId: string) => {
    // CRITICAL: Use the chunk's stored session ID if available
    // This ensures offline chunks go to their original session
    const targetSessionId = chunk.transcription_session_id || transcriptionSessionId;

    if (!targetSessionId) {
      console.error('No transcription session ID for chunk upload', {
        chunkId: chunk.id,
        chunkData: chunk,
      });
      throw new Error('No transcription session ID available for chunk upload');
    }

    const span = SentryService.startInactiveSpan('audio.upload', `Upload chunk ${chunk.id}`);
    span?.setAttribute('chunkId', chunk.id);
    span?.setAttribute('chunkPosition', String(chunk.position || 0));
    span?.setAttribute('transcriptionSessionId', targetSessionId);
    span?.setAttribute('originalSessionId', transcriptionSessionId);
    span?.setAttribute('storedSessionId', chunk.transcription_session_id);

    try {
      if (!chunk || chunk.position === undefined) {
        throw new Error('Not a valid chunk');
      }

      // Step 1: Read the chunk from storage
      let chunkBlob: Blob;
      const startReadTime = performance.now();

      if (useStorageServiceRef.current && storageServiceRef.current) {
        // V2 path: read ArrayBuffer from StorageService, wrap in Blob
        const ab = await storageServiceRef.current.readChunk(targetSessionId, chunk.position!);
        if (!ab) {
          span?.setStatus({ code: 2 });
          span?.setAttribute('error', `Chunk at position ${chunk.position} not found in StorageService`);
          throw new Error(`Chunk at position ${chunk.position} not found in StorageService`);
        }
        const mimeType = chunk.chunk_data?.chunk_type || 'audio/webm';
        chunkBlob = new Blob([ab], { type: mimeType });
      } else {
        // V1 path: read from IndexedDB
        const blob = await indexedDBManager.readBlob(chunk.id);
        if (!blob) {
          console.error(`Chunk with ID ${chunk.id} not found in IndexedDB`);
          span?.setStatus({ code: 2 });
          span?.setAttribute('error', `Chunk not found in IndexedDB`);
          throw new Error(`Chunk with ID ${chunk.id} not found in IndexedDB`);
        }
        chunkBlob = blob;
      }

      const readDuration = performance.now() - startReadTime;
      span?.setAttribute('storageReadTime', readDuration);
      SentryService.setMeasurement('chunk_read', readDuration, 'millisecond');

      const chunkFileExtension = chunkBlob.type.split('/')[1].split(';')[0];
      const chunkType = chunkBlob.type.split(';')[0];

      span?.setAttribute('chunkSize', chunkBlob.size);
      span?.setAttribute('chunkType', chunkType);

      SentryService.addBreadcrumb({
        category: 'audio.upload',
        message: `Read chunk ${chunk.id} from storage`,
        level: 'info',
        data: { size: chunkBlob.size, type: chunkType },
      });

      console.log(
        `Successfully read chunk ${chunk.id}. File type: ${chunkType}. ${chunkFileExtension}`,
      );

      // Step 3: Create the upload URL for the chunk
      console.log(`Creating upload URL for chunk ${chunk.id} at position ${chunk.position}`);

      const fileName = `${targetSessionId}/chunk${chunk.position}.${chunkFileExtension}`;

      SentryService.addBreadcrumb({
        category: 'audio.upload',
        message: `Creating presigned URL for chunk ${chunk.id}`,
        level: 'info',
        data: { fileName },
      });

      const getUploadURLResponse = await APIService.makeAPIPostRequest({
        requestString: `/transcriptionSession/createUploadURL`,
        accessToken: await getAccessToken(),
        body: {
          object_id: fileName,
          content_type: chunkType,
        },
        onNetworkDelay: () => {
          enterLowDataMode();
        },
      });

      if (!getUploadURLResponse.ok) {
        currentRecordingSession.current?.updateChunkUploadStatus(chunk.id, undefined, false);
        const apiMessage = getUploadURLResponse.error?.message || 'unknown';
        const getUploadURLError = new UploadError(
          `Getting upload URL failed: ${apiMessage}`,
          getUploadURLResponse.httpStatus,
          getUploadURLResponse.isNetworkError,
        );
        getUploadURLError.name = 'GetUploadURLError';

        span?.setStatus({ code: 2 });
        span?.setAttribute('error', 'Failed to get upload URL');
        span?.end();

        throw getUploadURLError;
      }

      const s3_object_id = getUploadURLResponse.value.s3_object_id;

      // Step 4: Upload the chunk to S3
      console.log(`Uploading chunk ${chunk.id} to S3`);

      SentryService.addBreadcrumb({
        category: 'audio.upload',
        message: `Uploading chunk ${chunk.id} to S3`,
        level: 'info',
        data: { s3_object_id },
      });

      const uploadResponse = await SentryService.traceFunctionAsync(
        'audio.upload.s3',
        `S3 upload chunk ${chunk.id}`,
        async s3UploadSpan => {
          const response = await uploadAudioAsync(
            chunkBlob,
            getUploadURLResponse.value.upload_url,
            chunkType,
            await getAccessToken(),
            'classic',
          );

          console.log('S3 upload response:', response);

          if (s3UploadSpan) {
            s3UploadSpan.setAttribute('uploadSuccess', response.success);
            if (!response.success) {
              s3UploadSpan.setAttribute('error', JSON.stringify(response));
              s3UploadSpan.setStatus({ code: 2 });
            } else {
              s3UploadSpan.setStatus({ code: 1 });
            }
          }

          return response;
        },
      );

      if (!uploadResponse.success) {
        currentRecordingSession.current?.updateChunkUploadStatus(chunk.id, s3_object_id, false);

        enterLowDataMode();

        // Notify about S3 upload error if callback is registered
        if (onS3UploadError) {
          const errorMessage = uploadResponse.error || 'Failed to upload audio to storage service';
          console.error('[RecordingContext] Calling onS3UploadError callback with:', errorMessage);
          onS3UploadError(errorMessage);
        } else {
          console.warn('[RecordingContext] No onS3UploadError callback registered');
        }

        const s3UploadError = new UploadError(`Uploading chunk to S3 failed. ${uploadResponse.error}`);
        s3UploadError.name = 'S3UploadError';

        span?.setStatus({ code: 2 });
        span?.setAttribute('error', `S3 upload error: ${uploadResponse.error}`);

        throw s3UploadError;
      } else {
        console.log(`Successfully uploaded to S3`);
        SentryService.addBreadcrumb({
          category: 'audio.upload',
          message: `Successfully uploaded chunk ${chunk.id} to S3`,
          level: 'info',
        });

        // Clear any S3 upload errors since we successfully uploaded
        if (onS3UploadError) {
          console.log('[RecordingContext] Clearing S3 error after successful upload');
          onS3UploadError('');
        }
      }

      // Step 5: Add chunk to Session
      console.log(`Adding chunk ${chunk.id} to transcription session ${targetSessionId}`);

      const success = await SentryService.traceFunctionAsync(
        'audio.upload.addChunk',
        `Add chunk to session ${chunk.id}`,
        async span => {
          const result = await addChunkToTranscriptionSession(
            targetSessionId,
            chunk.id,
            chunk.position!,
            s3_object_id,
            await getAccessToken(),
          );

          span.setAttribute('success', result);
          span.setStatus({ code: result ? 1 : 2 });

          return result;
        },
      );

      if (!success) {
        currentRecordingSession.current?.updateChunkUploadStatus(chunk.id, s3_object_id, false);
        const addChunkToSessionError = new Error('Failed to add chunk to transcription session');
        addChunkToSessionError.name = 'addChunkToSessionError';

        span?.setStatus({ code: 2 });
        span?.setAttribute('error', 'Failed to add chunk to transcription session');

        throw addChunkToSessionError;
      } else {
        // update upload status on chunk
        console.log(`Chunk ${chunk.id} added to transcription session successfully`);
        SentryService.addBreadcrumb({
          category: 'audio.upload',
          message: `Successfully added chunk ${chunk.id} to transcription session`,
          level: 'info',
        });

        // Clean up the chunk from storage after successful upload
        try {
          console.log(`Removing chunk ${chunk.id} from storage`);
          if (useStorageServiceRef.current && storageServiceRef.current) {
            await storageServiceRef.current.deleteChunk(targetSessionId, chunk.position!);
          } else {
            await indexedDBManager.deleteBlob(chunk.id);
          }
          console.log(`Successfully removed chunk ${chunk.id} from storage`);
          SentryService.addBreadcrumb({
            category: 'storage.cleanup',
            message: `Successfully removed chunk ${chunk.id} from storage`,
            level: 'info',
          });
        } catch (error) {
          // Don't throw error on cleanup failure, just log it
          console.warn(`Failed to remove chunk ${chunk.id} from storage:`, error);
          SentryService.addBreadcrumb({
            category: 'storage.cleanup',
            message: `Failed to remove chunk ${chunk.id} from storage: ${error}`,
            level: 'warning',
          });
        }
      }

      // If everything succeeded, update chunk as successful
      currentRecordingSession.current?.updateChunkUploadStatus(chunk.id, s3_object_id, success);

      span?.setStatus({ code: 1 });
    } catch (error) {
      console.error(`Upload Chunk Error: ${error}`);
      const errorName = error instanceof Error ? error.name : 'UnknownError';
      SentryService.logEvent(`Upload Chunk Error: ${errorName}`, {
        level: 'error',
        tags: {
          upload_error_type: errorName,
          upload_http_status: String(error instanceof UploadError ? error.httpStatus ?? 'none' : 'none'),
          upload_is_network_error: String(error instanceof UploadError ? error.isNetworkError ?? 'none' : 'none'),
        },
        extra: {
          error,
          transcriptionSessionId: currentTranscriptionSessionId,
          httpStatus: error instanceof UploadError ? error.httpStatus : undefined,
          isNetworkError: error instanceof UploadError ? error.isNetworkError : undefined,
          errorName,
        },
      });

      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));

      throw new Error(`Upload Chunk Error: ${error}`);
    } finally {
      span?.end();
    }
  };

  // --- UploadService event handlers (use refs to avoid stale closures) ---
  const handleUploadServiceChunkUploaded = useCallback(
    (data: { sessionId: string; chunkIndex: number; s3ObjectId: string }) => {
      const session = currentRecordingSession.current;
      if (!session) return;
      const chunk = session.chunks.find((c) => c.position === data.chunkIndex);
      if (chunk) {
        session.updateChunkUploadStatus(chunk.id, data.s3ObjectId, true);
      }
      // Clear any previous S3 error on successful upload
      onS3UploadErrorRef.current?.('');

      // V5: update manifest with uploaded chunk
      if (useSessionCompletionServiceRef.current && sessionManifestRef.current) {
        sessionManifestRef.current.markChunkUploaded(data.sessionId, data.chunkIndex).catch(() => {});
      }
    },
    [],
  );

  const handleUploadServiceChunkFailed = useCallback(
    (data: { sessionId: string; chunkIndex: number; error: string; willRetry: boolean }) => {
      if (data.willRetry) return; // will be retried, no action needed yet
      const session = currentRecordingSession.current;
      if (!session) return;
      const chunk = session.chunks.find((c) => c.position === data.chunkIndex);
      if (chunk) {
        session.updateChunkUploadStatus(chunk.id, undefined, false);
      }
      onS3UploadErrorRef.current?.(data.error);
    },
    [],
  );

  const handleUploadServiceChunkDeadLettered = useCallback(
    (data: { entry: { sessionId: string; chunkIndex: number; error: string } }) => {
      const session = currentRecordingSession.current;
      if (!session) return;
      const chunk = session.chunks.find((c) => c.position === data.entry.chunkIndex);
      if (chunk) {
        session.updateChunkUploadStatus(chunk.id, undefined, false);
      }
      onS3UploadErrorRef.current?.(data.entry.error);

      // V5: update manifest with failed chunk
      if (useSessionCompletionServiceRef.current && sessionManifestRef.current) {
        sessionManifestRef.current.markChunkFailed(data.entry.sessionId, data.entry.chunkIndex).catch(() => {});
      }
    },
    [],
  );

  const handleUploadServiceNetworkChanged = useCallback(
    (data: { isOnline: boolean }) => {
      if (!data.isOnline) {
        enterLowDataMode();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleUploadServiceQueueDrained = useCallback(() => {
    const sessionId = pendingFinalizationRef.current;
    if (!sessionId) return;
    pendingFinalizationRef.current = null;
    void attemptEndRecordingV3(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- RecordingService event handlers ---

  const handleRecordingServiceChunkReady = useCallback(
    (data: RecordingEventMap['chunkReady']) => {
      if (!currentRecordingSession.current) {
        SentryService.logEvent('RecordingService chunkReady but no active session', {
          level: 'warning',
          extra: { sessionId: data.sessionId, chunkIndex: data.chunkIndex },
        });
        return;
      }

      try {
        currentRecordingSession.current.addChunkToRecordingSession({
          id: data.chunkId,
          start_timestamp_seconds: Math.max(
            0,
            elapsedTime.current - DEFAULT_TIMESLICE_MS / 1000,
          ),
          end_timestamp_seconds: elapsedTime.current,
          upload_status: 'pending',
          chunk_data: {
            chunk_size: data.byteSize,
            chunk_type: data.contentType,
          },
          position: data.chunkIndex,
          transcription_session_id: data.sessionId,
        });

        // RecordingService already enqueued to UploadService — just track the count
        enqueuedChunkCountRef.current++;

        // V5: update manifest with new chunk
        if (useSessionCompletionServiceRef.current && sessionManifestRef.current) {
          sessionManifestRef.current.incrementTotalChunks(data.sessionId).catch(() => {});
        }

        if (isRecordingEnding.current && !useSessionCompletionServiceRef.current) {
          // V3/V4: wait for UploadService queueDrained → attemptEndRecordingV3
          // V5: SessionCompletionService.startCompletion() handles drain + recording-end
          pendingFinalizationRef.current = data.sessionId;
        }
      } catch (error) {
        SentryService.logEvent('RecordingService chunkReady handler error', {
          level: 'error',
          extra: { error, sessionId: data.sessionId, chunkIndex: data.chunkIndex },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleRecordingServiceError = useCallback(
    (data: RecordingEventMap['error']) => {
      SentryService.logEvent('RecordingService error', {
        level: 'error',
        extra: { message: data.message, cause: data.cause },
      });
      enqueueSnackbar(`Recording error: ${data.message}`, {
        variant: 'error',
        preventDuplicate: true,
      });
      recordingTracking.error(data.message, {
        session_id: transcriptionSessionIdRef.current ?? undefined,
        recording_pipeline: 'streaming',
      });
    },
    [enqueueSnackbar, recordingTracking],
  );

  const handleRecordingServiceChunkError = useCallback(
    (data: RecordingEventMap['chunkError']) => {
      SentryService.logEvent('RecordingService chunk error', {
        level: 'error',
        extra: { chunkId: data.chunkId, error: data.error },
      });
      currentRecordingSession.current?.addLog('RecordingService Chunk Error', {
        chunk_id: data.chunkId,
        error: data.error,
      });

      // If recording is ending and nothing was enqueued, finalize immediately
      if (isRecordingEnding.current) {
        const sessionId =
          currentRecordingSession.current?.transcription_session_id;
        if (sessionId) {
          if (enqueuedChunkCountRef.current === 0) {
            void attemptEndRecordingV3(sessionId);
          } else {
            pendingFinalizationRef.current = sessionId;
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleRecordingServiceAudioLevel = useCallback(
    (data: RecordingEventMap['audioLevel']) => {
      // Same scaling as V1-V3 onAudioImpulse callback (lines 1334-1351)
      const averageLevel = data.level;
      let finalValue: number;

      if (averageLevel <= 10) {
        finalValue = averageLevel;
      } else if (averageLevel <= 100) {
        const percent = (averageLevel - 10) / 90;
        finalValue = 10 + percent * 80;
      } else {
        const percent = (averageLevel - 100) / 155;
        finalValue = 90 + percent * 10;
      }

      setAudioData(finalValue);
    },
    [setAudioData],
  );

  // Initialize/dispose UploadService when gate enabled (requires StorageService)
  useEffect(() => {
    if (!useUploadService) return;

    // UploadService requires StorageService — force off if unavailable
    if (!storageServiceRef.current) {
      setUseUploadService(false);
      return;
    }

    const service = new UploadService({
      apiBaseUrl: API_BASE_URL,
      getAccessToken,
      storageService: storageServiceRef.current,
      recordingPipeline: 'streaming',
    });
    uploadServiceRef.current = service;
    setExposedUploadService(service);

    service.on('chunkUploaded', handleUploadServiceChunkUploaded);
    service.on('chunkFailed', handleUploadServiceChunkFailed);
    service.on('chunkDeadLettered', handleUploadServiceChunkDeadLettered);
    service.on('networkChanged', handleUploadServiceNetworkChanged);
    service.on('queueDrained', handleUploadServiceQueueDrained);

    service.start();

    SentryService.addBreadcrumb({
      category: 'upload.service',
      message: 'UploadService initialized',
      level: 'info',
    });

    return () => {
      service.off('chunkUploaded', handleUploadServiceChunkUploaded);
      service.off('chunkFailed', handleUploadServiceChunkFailed);
      service.off('chunkDeadLettered', handleUploadServiceChunkDeadLettered);
      service.off('networkChanged', handleUploadServiceNetworkChanged);
      service.off('queueDrained', handleUploadServiceQueueDrained);
      service.dispose();
      uploadServiceRef.current = null;
      setExposedUploadService(null);
    };
  }, [
    useUploadService,
    getAccessToken,
    handleUploadServiceChunkUploaded,
    handleUploadServiceChunkFailed,
    handleUploadServiceChunkDeadLettered,
    handleUploadServiceNetworkChanged,
    handleUploadServiceQueueDrained,
  ]);

  // Initialize/dispose RecordingService when gate enabled (requires StorageService + UploadService)
  useEffect(() => {
    if (!useRecordingService) return;

    // RecordingService requires both StorageService and UploadService — force off if unavailable
    if (!storageServiceRef.current || !uploadServiceRef.current) {
      setUseRecordingService(false);
      return;
    }

    const service = new RecordingService({
      storageService: storageServiceRef.current,
      uploadService: uploadServiceRef.current,
    });
    recordingServiceRef.current = service;
    setExposedRecordingService(service);

    service.on('chunkReady', handleRecordingServiceChunkReady);
    service.on('error', handleRecordingServiceError);
    service.on('chunkError', handleRecordingServiceChunkError);
    service.on('audioLevel', handleRecordingServiceAudioLevel);

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: 'RecordingService initialized',
      level: 'info',
    });

    return () => {
      service.off('chunkReady', handleRecordingServiceChunkReady);
      service.off('error', handleRecordingServiceError);
      service.off('chunkError', handleRecordingServiceChunkError);
      service.off('audioLevel', handleRecordingServiceAudioLevel);
      service.dispose();
      recordingServiceRef.current = null;
      setExposedRecordingService(null);
    };
  }, [
    useRecordingService,
    handleRecordingServiceChunkReady,
    handleRecordingServiceError,
    handleRecordingServiceChunkError,
    handleRecordingServiceAudioLevel,
  ]);

  // Initialize/dispose SessionCompletionService + SessionRecoveryService (ADR-007)
  // Requires StorageService + UploadService + SessionManifest
  useEffect(() => {
    if (!useSessionCompletionService) return;

    if (!storageServiceRef.current || !uploadServiceRef.current) {
      setUseSessionCompletionService(false);
      return;
    }

    let disposed = false;

    const manifest = new SessionManifest();
    sessionManifestRef.current = manifest;

    const init = async () => {
      await manifest.open();
      if (disposed) {
        await manifest.close();
        return;
      }

      // Wait for StorageService to finish initializing — it may still be
      // in 'initializing' state because the storage effect starts init async.
      const storage = storageServiceRef.current!;
      const storageStatus = storage.getSnapshot().status;
      if (storageStatus !== 'ready') {
        if (storageStatus === 'error') {
          await manifest.close();
          return;
        }
        await new Promise<void>((resolve, reject) => {
          const unsub = storage.subscribe(() => {
            const s = storage.getSnapshot();
            if (s.status === 'ready') {
              unsub();
              resolve();
            } else if (s.status === 'error') {
              unsub();
              reject(new Error(`StorageService failed: ${s.error}`));
            }
          });
        });
      }

      if (disposed) {
        await manifest.close();
        return;
      }

      const completionService = new SessionCompletionService({
        uploadService: uploadServiceRef.current!,
        storageService: storageServiceRef.current!,
        sessionManifest: manifest,
        getAccessToken,
      });
      sessionCompletionServiceRef.current = completionService;
      setExposedCompletionService(completionService);

      const recoveryService = new SessionRecoveryService({
        storageService: storageServiceRef.current!,
        uploadService: uploadServiceRef.current!,
        completionService,
        sessionManifest: manifest,
      });
      sessionRecoveryServiceRef.current = recoveryService;
      setExposedRecoveryService(recoveryService);

      // Reset stale recordingStatus when recovery finds orphaned sessions.
      // After a crash, localStorage still has isRecording: true — fix that.
      recoveryService.on('scanComplete', ({ found, recovered, failed, cleaned }) => {
        if (found > 0) {
          // Don't reset recording status if a recording started after the scan began
          const serviceActive = recordingServiceRef.current?.getSnapshot().status === 'recording';
          const legacyActive = !!webRecordingInstance.current?.recorder;
          if (serviceActive || legacyActive) return;

          setRecordingStatus(prev => ({
            ...prev,
            isRecording: false,
            isStarting: false,
            isStopping: false,
            readyToRecord: true,
          }));
        }
        sessionRecoveryTracking.scanCompleted({ found, recovered, failed, cleaned });
      });

      recoveryService.on('sessionRecovered', ({ result }) => {
        sessionRecoveryTracking.sessionRecovered({
          session_id: result.sessionId,
          total_chunks: result.totalChunks,
          uploaded_chunks: result.uploadedChunks,
          failed_chunks: result.failedChunks,
        });
      });

      recoveryService.on('sessionFailed', ({ sessionId, error }) => {
        sessionRecoveryTracking.sessionFailed({ session_id: sessionId, error });
      });

      recoveryService.on('staleManifestCleaned', ({ sessionId, reason, totalChunks }) => {
        sessionRecoveryTracking.staleManifestCleaned({
          session_id: sessionId,
          reason,
          total_chunks: totalChunks,
        });
      });

      SentryService.addBreadcrumb({
        category: 'session.completion',
        message: 'SessionCompletionService + SessionRecoveryService initialized',
        level: 'info',
      });

      // Log if the browser discarded the previous page (tab was killed after hours backgrounded)
      if ((document as { wasDiscarded?: boolean }).wasDiscarded) {
        SentryService.addBreadcrumb({
          category: 'recording.lifecycle',
          message: 'Previous page was discarded by browser',
          level: 'error',
        });
      }

      // Auto-scan for orphaned sessions on initialization
      recoveryService.scan().catch((error) => {
        SentryService.captureException(
          error instanceof Error ? error : new Error(String(error)),
        );
      });
    };

    init().catch((error) => {
      SentryService.captureException(
        error instanceof Error ? error : new Error(`Session services init failed: ${String(error)}`),
      );
      disableStreamingPipeline('session_completion', error);
    });

    return () => {
      disposed = true;
      sessionRecoveryServiceRef.current?.dispose();
      sessionRecoveryServiceRef.current = null;
      setExposedRecoveryService(null);
      sessionCompletionServiceRef.current?.dispose();
      sessionCompletionServiceRef.current = null;
      setExposedCompletionService(null);
      manifest.close().catch(() => {});
      sessionManifestRef.current = null;
    };
  }, [useSessionCompletionService, getAccessToken]);

  // Handle a recording chunk
  const handleChunk = async (chunkId: string, transcriptionSessionId: string) => {
    try {
      if (!transcriptionSessionId) {
        console.error('No transcription session id');
        throw new Error(`No transcription session id`);
      }

      chunkPosition++;
      console.log(
        `handleChunk called with chunkId: ${chunkId}, sessionId: ${transcriptionSessionId}`,
      );

      // Step 1: Get chunk metadata — V2 uses pending blob map, V1 reads from IndexedDB
      let chunkSize: number;
      let chunkType: string;
      let pendingBlob: Blob | undefined;

      if (useStorageServiceRef.current && storageServiceRef.current) {
        // V2 path: retrieve blob from pending map (write deferred until after position assigned)
        pendingBlob = pendingChunkBlobsRef.current.get(chunkId);
        if (!pendingBlob) {
          throw new Error(`Pending blob for chunk ${chunkId} not found`);
        }
        pendingChunkBlobsRef.current.delete(chunkId);

        chunkSize = pendingBlob.size;
        chunkType = pendingBlob.type;
      } else {
        // V1 path: read from IndexedDB
        const chunkBlob = await indexedDBManager.readBlob(chunkId);
        if (!chunkBlob) {
          console.error(`Chunk with ID ${chunkId} not found in IndexedDB`);
          throw new Error(`Chunk with ID ${chunkId} not found in IndexedDB`);
        }
        chunkSize = chunkBlob.size;
        chunkType = chunkBlob.type;
      }

      let newChunk;
      try {
        if (!currentRecordingSession.current) {
          throw new Error('No current recording session.');
        }

        newChunk = currentRecordingSession.current.addChunkToRecordingSession({
          id: chunkId,
          start_timestamp_seconds: Math.max(
            0,
            elapsedTime.current - RECORDING_CHUNK_LENGTH_SECONDS,
          ),
          end_timestamp_seconds: elapsedTime.current,
          upload_status: 'pending',
          chunk_data: {
            chunk_size: chunkSize,
            chunk_type: chunkType,
          },
          position: chunkPosition,
          // CRITICAL: Store the transcription session ID with the chunk
          transcription_session_id: transcriptionSessionId,
        });
      } catch (error) {
        SentryService.logEvent('Chunk creation error', {
          level: 'error',
          extra: { error, transcriptionSessionId },
        });
        throw new Error(`Chunk creation error: ${error}`);
      }

      // V2: Write to StorageService AFTER addChunkToRecordingSession, which assigns
      // the final 0-based position (chunk.position = this.chunks.length).
      // We must use newChunk.position so read/delete use the same key.
      if (pendingBlob && storageServiceRef.current) {
        const arrayBuffer = await pendingBlob.arrayBuffer();
        await storageServiceRef.current.writeChunk(
          transcriptionSessionId,
          newChunk.position!,
          arrayBuffer,
        );
      }

      // Upload the chunk
      if (useUploadServiceRef.current && uploadServiceRef.current) {
        // V3: fire-and-forget enqueue — UploadService handles retry, concurrency, events
        uploadServiceRef.current.enqueue({
          sessionId: newChunk.transcription_session_id || transcriptionSessionId,
          chunkIndex: newChunk.position!,
          chunkId: newChunk.id,
          contentType: chunkType,
          byteSize: chunkSize,
        });
        enqueuedChunkCountRef.current++;

        if (isRecordingEnding.current) {
          // Don't finalize now — wait for queueDrained event
          pendingFinalizationRef.current = transcriptionSessionId;
        }
      } else {
        // V1/V2: synchronous upload per chunk
        await uploadChunk(newChunk, newChunk.transcription_session_id || transcriptionSessionId);

        if (isRecordingEnding.current) {
          console.log('Last chunk handled, attempting to end recording...');
          await attemptEndRecording(transcriptionSessionId);
        }
      }
    } catch (error) {
      console.error('Error handling chunk', error);
      currentRecordingSession.current?.addLog('Chunk Error', {
        chunk_id: chunkId,
        error: error,
      });
      SentryService.logEvent('Error handling chunk.', {
        level: 'error',
        extra: {
          chunkId,
          error,
          transcriptionSessionId,
        },
      });
      // Check if this was the last chunk to process
      if (isRecordingEnding.current) {
        if (useUploadServiceRef.current && uploadServiceRef.current) {
          // V3: if nothing was enqueued, finalize immediately; otherwise wait for drain
          if (enqueuedChunkCountRef.current === 0) {
            await attemptEndRecordingV3(transcriptionSessionId);
          } else {
            pendingFinalizationRef.current = transcriptionSessionId;
          }
        } else {
          console.log('Last chunk failed to be handled, attempting to end recording...');
          await attemptEndRecording(transcriptionSessionId);
        }
      }
    }
  };

  // Handle a chunk error
  const handleChunkError = async (chunkId: string, error: unknown) => {
    const errorMessage = `Recording error. Please try again.`;
    setAudioErrorMessage(errorMessage);
    setTimeout(() => setAudioErrorMessage(''), 3000);
    enqueueSnackbar(errorMessage, { variant: 'error' });

    // Only log to Sentry if this error wasn't already captured upstream
    // (e.g., blob validation errors are logged at the primary error site in WebRecordingService)
    if (!(error as any)?._sentryLogged) {
      SentryService.logEvent('Chunk error.', {
        level: 'error',
        extra: { error, transcriptionSessionId: currentTranscriptionSessionId },
      });
    }

    // Check if this was the last chunk to process
    if (isRecordingEnding.current) {
      console.log('Last chunk failed to be handled, attempting to end recording...');
      await attemptEndRecording(currentTranscriptionSessionId);
    }

    // need to do some specific "cleanup" here. stop recording and allow user to restart.

    // just putting cleanUpSession for now, temporary placeholder
    // cleanUpSession();
  };

  // Finalize the transcription session
  const finalizeTranscriptionSession = async (transcrptionId: string): Promise<void> => {
    try {
      SentryService.setTag('recordingPhase', 'finalization');
      console.log('Finalizing transcription session');
      const endTranscriptionSessionStatus = await endTranscriptionSession(
        transcrptionId,
        currentRecordingSession.current?.generateLogsString() || '',
        await getAccessToken(),
      );
      console.log('Transcription Session Has ended:', endTranscriptionSessionStatus);

      // Clear chunk storage
      if (useStorageServiceRef.current && storageServiceRef.current) {
        await storageServiceRef.current.deleteSession(transcrptionId);
      } else {
        await indexedDBManager.clearAll();
      }
      console.log('Chunk storage cleared');

      // Call the finalized callback to create document BEFORE setting readyToSubmit
      // This ensures the document exists before the user can click "Generate Note"
      if (onRecordingFinalizedRef.current) {
        try {
          console.log('Calling onRecordingFinalized callback');
          await onRecordingFinalizedRef.current(transcrptionId, elapsedTime.current);
          console.log('onRecordingFinalized callback completed');
        } catch (error) {
          console.error('Error in onRecordingFinalized callback:', error);
          SentryService.logEvent('Error in onRecordingFinalized callback', {
            level: 'error',
            extra: { error, transcriptionSessionId: transcrptionId },
          });
          // Continue with finalization even if document creation fails
          // The validation will catch the missing document
        }
      }

      SentryService.setTag('recordingPhase', 'completed');
      // Reset state variables - NOW safe to set readyToSubmit
      setRecordingStatus(prev => ({
        ...prev,
        isRecording: false,
        readyToRecord: false,
        readyToSubmit: true,
        isStopping: false,
      }));

      // Clear recording starter ID so other tabs see recording has ended
      const currentStarterId = localStorage.getItem('recording_starter_id');
      const currentTabId = tabCommunicator.getTabId();
      if (currentStarterId === currentTabId) {
        localStorage.removeItem('recording_starter_id');
      }

      setAudioData(0);
      isRecordingEnding.current = false;
      chunkPosition = 0;

      if (shouldBeCleanedUp.current) {
        console.log('Session should be cleaned up');
        cleanUpSession();
      }
    } catch (error) {
      shouldBeCleanedUp.current = false; // prevent leak to next session
      console.error('Error finalizing transcription session', error);
      SentryService.logEvent('Error finalizing transcription session', {
        level: 'error',
        extra: { error, transcriptionSessionId: currentTranscriptionSessionId },
      });
      setRecordingStatus(prev => ({ ...prev, isStopping: false }));
    }
  };

  // Function to attempt to end the recording
  const attemptEndRecording = async (transcriptionSessionId: string) => {
    const endSessionStatus = await currentRecordingSession.current?.endSession();
    console.log('Recording Session End Status:', endSessionStatus);

    try {
      // If there are failed chunks, move into `chunksToBeUploaded` state
      if (endSessionStatus?.failedChunks && endSessionStatus.failedChunks.length > 0) {
        setRecordingStatus(prev => ({
          ...prev,
          chunksToBeUploaded: true,
        }));

        try {
          // Try to reupload chunks
          await reuploadChunks(endSessionStatus.failedChunks, transcriptionSessionId);
        } catch (e) {
          console.error("Couldn't finish reupload.");
        }

        setRecordingStatus(prev => ({
          ...prev,
          isStopping: false,
        }));
      } else {
        // If everything was successfully uploaded, finalize
        finalizeTranscriptionSession(transcriptionSessionId);
      }
    } catch (error) {
      console.error(`Error attempting end recording: ${error}`);
      SentryService.logEvent('Error attempting end recording.', {
        level: 'error',
        extra: { error, transcriptionSessionId: currentTranscriptionSessionId },
      });
      setRecordingStatus(prev => ({ ...prev, isStopping: false }));
    }
  };

  // V3 end-recording: called from queueDrained handler when UploadService finishes all uploads
  const attemptEndRecordingV3 = async (transcriptionSessionId: string) => {
    const endSessionStatus = await currentRecordingSession.current?.endSession();
    console.log('Recording Session End Status (V3):', endSessionStatus);

    try {
      if (endSessionStatus?.failedChunks && endSessionStatus.failedChunks.length > 0) {
        // Show retry UI — user clicks → initiateReupload
        setRecordingStatus(prev => ({
          ...prev,
          chunksToBeUploaded: true,
          isStopping: false,
        }));
      } else {
        finalizeTranscriptionSession(transcriptionSessionId);
      }
    } catch (error) {
      console.error(`Error attempting end recording (V3): ${error}`);
      SentryService.logEvent('Error attempting end recording (V3).', {
        level: 'error',
        extra: { error, transcriptionSessionId },
      });
      setRecordingStatus(prev => ({ ...prev, isStopping: false }));
    }
  };

  // Internal function to perform the actual stop recording logic
  // This is called by both initiateEndRecording (with guard) and requestRemoteStop (without guard)
  const performStopRecording = async (): Promise<boolean> => {
    try {
      let shortRecording;

      if (!useRecordingServiceRef.current) {
        if (!webRecordingInstance.current) {
          webRecordingInstance.current = new WebRecordingService();
        }
      }

      if (elapsedTime.current < 5) {
        shortRecording = true;
      }

      const recorderState = useRecordingServiceRef.current
        ? undefined
        : webRecordingInstance.current?.recorder?.getState();

      isRecordingEnding.current = true;
      SentryService.setTag('recordingPhase', 'stopping');
      setAudioData(0);
      setRecordingStatus(prev => {
        const newStatus = {
          ...prev,
          isRecording: false,
          isStopping: true,
        };
        sendMessageToExtension({ type: 'recordingStatus', data: newStatus });
        return newStatus;
      });

      // ============================================================================
      // CRITICAL: Capture session ID BEFORE stopping the recorder
      // ============================================================================
      // The stop() call yields to microtask queues and can allow state/refs to be
      // cleared (especially during tab takeover). Capture the ID while everything
      // is still alive.
      //
      // Fallback chain (in order of reliability):
      // 1. Ref - stable reference set in performStartRecording, not affected by closures
      // 2. localStorage direct read - avoids React state/closure staleness
      // 3. React state - may be stale in useCallback contexts
      // 4. RecordingSession object - last resort, may also be cleared
      // ============================================================================
      let effectiveSessionId = transcriptionSessionIdRef.current;

      if (!effectiveSessionId) {
        try {
          const storedSessionId = localStorage.getItem('transcriptionSessionId');
          if (storedSessionId) {
            effectiveSessionId = JSON.parse(storedSessionId);
          }
        } catch (e) {
          console.warn('Failed to parse transcriptionSessionId from localStorage:', e);
        }
      }

      if (!effectiveSessionId && currentTranscriptionSessionId) {
        effectiveSessionId = currentTranscriptionSessionId;
      }

      if (!effectiveSessionId && currentRecordingSession.current?.transcription_session_id) {
        effectiveSessionId = currentRecordingSession.current.transcription_session_id;
      }

      // Now stop the recorder — session ID is already captured
      if (useRecordingServiceRef.current && recordingServiceRef.current) {
        // V5 path: RecordingService flushes final data, finalizes remaining chunks, stops MediaRecorder.
        // CRITICAL: stop() MUST complete before mixer cleanup — closing AudioContext before
        // MediaRecorder.stop() corrupts the final chunk.
        await recordingServiceRef.current.stop();
        streamCleanupRef.current?.();
        streamCleanupRef.current = null;
        mixerResultRef.current = null;
      } else {
        await webRecordingInstance.current?.stopMediaStreamRecording(shortRecording);
      }

      // Analytics: recording stopped (both pipelines)
      recordingTracking.stop({
        session_id: effectiveSessionId ?? transcriptionSessionIdRef.current ?? undefined,
        recording_pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
        duration_seconds: elapsedTime.current,
      });

      // NOTE: We intentionally do NOT release the AudioInputDeviceService stream here.
      // The stream stays alive so the level meter continues showing mic activity between
      // recordings (waiting room pattern). Release only happens on page navigation,
      // session cleanup, or tab visibility change.

      if (!effectiveSessionId) {
        const localStorageRaw = localStorage.getItem('transcriptionSessionId');
        console.error('No transcription session ID found after all fallbacks.', {
          reactState: currentTranscriptionSessionId,
          ref: transcriptionSessionIdRef.current,
          localStorage: localStorageRaw,
          recordingSession: currentRecordingSession.current?.transcription_session_id,
        });
        SentryService.logEvent('No transcription session ID found - all fallbacks failed', {
          level: 'error',
          extra: {
            reactState: currentTranscriptionSessionId,
            refValue: transcriptionSessionIdRef.current,
            localStorageRaw,
            hasRecordingSession: !!currentRecordingSession.current,
            pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
            elapsedTime: elapsedTime.current,
            recordingServiceStatus: recordingServiceRef.current
              ? 'exists'
              : 'null',
          },
        });
        // NOTE: We intentionally do NOT call cleanUpSession() here anymore.
        // Previously, calling cleanUpSession() would delete the recording data from IndexedDB,
        // causing the upload to fail with "Chunk not found in IndexedDB" error.
        // By not cleaning up, we preserve the data and allow retry mechanisms to work.
        if (useSessionCompletionServiceRef.current) {
          sessionCompletionTracking.fatalError({
            session_id: undefined,
            error_type: 'no_session_id',
            duration_seconds: elapsedTime.current,
          });
        }
        return false;
      }

      currentRecordingSession.current?.addMediaStreamInfo({
        streamEndedAt: new Date().toISOString(),
      });

      if (shortRecording) {
        enqueueSnackbar(
          'This recording is too short to be processed. If you are just testing JotPsych, please submit a recording at least a minute in length.',
          { variant: 'error' },
        );
        SentryService.logEvent('Short Recording', {
          level: 'info',
          extra: { elapsedTime: elapsedTime.current },
        });
        recordingTracking.shortDismissed({
          session_id: transcriptionSessionIdRef.current ?? undefined,
          encounter_id: recordingEncounterIdRef.current ?? undefined,
          duration_seconds: elapsedTime.current,
          recording_pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
        });

        // Track as a completion event so every recording_started has a corresponding terminal event
        if (useSessionCompletionServiceRef.current) {
          sessionCompletionTracking.finished({
            session_id: effectiveSessionId,
            total_chunks: 0,
            uploaded_chunks: 0,
            failed_chunks: 0,
            failed_chunks_ratio: 0,
            drain_duration_seconds: 0,
            timed_out: false,
            recording_end_confirmed: false,
            status: 'short_recording_discarded',
          });
        }

        cleanUpSession();
        return true;
      }

      if (useSessionCompletionServiceRef.current && sessionCompletionServiceRef.current) {
        // V5 path: Non-blocking drain — transition to "stopped" immediately,
        // completion service runs in background and updates its own state via useSyncExternalStore.
        const completionService = sessionCompletionServiceRef.current;
        const totalChunks = enqueuedChunkCountRef.current;
        const capturedSessionId = effectiveSessionId;

        // Immediately mark as ready — triggers document creation via polling effect in EncounterViewV2.
        // Generate Note is separately gated by canGenerateNote from SessionCompletionService.
        setRecordingStatus(prev => ({
          ...prev,
          isStopping: false,
          readyToSubmit: true,
        }));

        // Invoke onRecordingStopped callback — creates the recording document BEFORE
        // any caller (e.g. TelehealthCallManager) proceeds with cleanup.
        if (onRecordingStoppedRef.current) {
          try {
            await onRecordingStoppedRef.current(capturedSessionId, elapsedTime.current);
          } catch (error) {
            console.error('Error in onRecordingStopped callback:', error);
            SentryService.logEvent('Error in onRecordingStopped callback', {
              level: 'error',
              extra: { error, transcriptionSessionId: capturedSessionId },
            });
          }
        }

        // Fire-and-forget: drain + recording-end run in background.
        // Storage + manifest cleanup is handled inside completeSession() (before status transition).
        const recordingSessionLogs = currentRecordingSession.current?.generateLogsString() || '';
        completionService.startCompletion(capturedSessionId, totalChunks, recordingSessionLogs)
          .then(async (result) => {
            // Call the finalized callback if set (V1 EncounterRecordingWidget uses this)
            if (onRecordingFinalizedRef.current) {
              try {
                await onRecordingFinalizedRef.current(capturedSessionId, elapsedTime.current);
              } catch (error) {
                console.error('Error in onRecordingFinalized callback:', error);
                SentryService.logEvent('Error in onRecordingFinalized callback (V5)', {
                  level: 'error',
                  extra: { error, transcriptionSessionId: capturedSessionId },
                });
              }
            }

            SentryService.setTag('recordingPhase', 'completed');
            SentryService.addBreadcrumb({
              category: 'session.completion',
              message: `V5 completion done: uploaded=${result.uploadedChunks}, failed=${result.failedChunks}, timedOut=${result.timedOut}`,
              level: 'info',
            });

            // Analytics: drain phase completed
            sessionCompletionTracking.drainCompleted({
              session_id: capturedSessionId,
              drain_duration_ms: result.drainDurationMs,
              completed_count: result.uploadedChunks,
              failed_count: result.failedChunks,
              timed_out: result.timedOut,
            });

            // Analytics: session completion finished
            sessionCompletionTracking.finished({
              session_id: capturedSessionId,
              total_chunks: result.totalChunks,
              uploaded_chunks: result.uploadedChunks,
              failed_chunks: result.failedChunks,
              failed_chunks_ratio: result.totalChunks > 0
                ? result.failedChunks / result.totalChunks
                : 0,
              drain_duration_seconds: result.drainDurationMs / 1000,
              timed_out: result.timedOut,
              recording_end_confirmed: result.recordingEndConfirmed,
              status: result.failedChunks > 0 ? 'completed_with_losses' : 'completed',
            });

            // Analytics: timeout event (separate for easy dashboard filtering)
            if (result.timedOut) {
              sessionCompletionTracking.timeout({
                session_id: capturedSessionId,
                queued_at_timeout: uploadServiceRef.current?.getSnapshot().queueDepth,
                in_flight_at_timeout: uploadServiceRef.current?.getSnapshot().inFlightCount,
              });
            }
          })
          .catch((error) => {
            console.error('SessionCompletionService.startCompletion failed:', error);
            SentryService.captureException(
              error instanceof Error ? error : new Error(String(error)),
            );

            // Analytics: network failure during completion
            sessionCompletionTracking.networkFailure({
              session_id: capturedSessionId,
              pending_chunks: uploadServiceRef.current?.getSnapshot().queueDepth,
            });
            sessionCompletionTracking.fatalError({
              session_id: capturedSessionId,
              error_type: 'completion_failed',
              error_message: (error instanceof Error ? error.message : String(error)).slice(0, 200),
              pending_chunks: uploadServiceRef.current?.getSnapshot().queueDepth,
              duration_seconds: elapsedTime.current,
            });
          });
      } else if (!useRecordingServiceRef.current && recorderState === 'recording') {
        console.log(
          'Chunks pending. Waiting for the last chunk to be handled before ending the session',
        );
      } else {
        // No more pending chunks — but uploads may still be in-flight (V3/V4)
        if (useUploadServiceRef.current && uploadServiceRef.current) {
          const uploadState = uploadServiceRef.current.getSnapshot();
          if (uploadState.queueDepth > 0 || uploadState.inFlightCount > 0) {
            // Uploads still in flight — wait for queueDrained event
            pendingFinalizationRef.current = effectiveSessionId;
          } else if (enqueuedChunkCountRef.current === 0) {
            // No chunks were produced at all — finalize immediately
            await attemptEndRecordingV3(effectiveSessionId);
          } else {
            // All uploads already drained — finalize now
            await attemptEndRecordingV3(effectiveSessionId);
          }
        } else {
          console.log('No more pending chunks, attempting to end recording...');
          await attemptEndRecording(effectiveSessionId);
        }
      }

      return true;
    } catch (error) {
      console.error('Error initiating end recording', error);
      SentryService.logEvent('Error initiating end recording.', {
        level: 'error',
        extra: { error, transcriptionSessionId: currentTranscriptionSessionId },
      });
      if (useSessionCompletionServiceRef.current) {
        sessionCompletionTracking.fatalError({
          session_id: transcriptionSessionIdRef.current || undefined,
          error_type: 'stop_failed',
          error_message: (error instanceof Error ? error.message : String(error)).slice(0, 200),
          duration_seconds: elapsedTime.current,
        });
      }
      return false;
    }
  };

  // Keep ref in sync so requestRemoteStop (stale useCallback) always calls the latest version
  performStopRecordingRef.current = performStopRecording;

  // Update initiateEndRecording method to use tabCommunicator
  const initiateEndRecording = async (): Promise<boolean> => {
    // Only allow the owner tab to end the recording
    if (!allowRecorderControl) {
      enqueueSnackbar('Only the tab that started the recording can control it', {
        variant: 'warning',
      });
      return false;
    }

    return performStopRecording();
  };

  // Initiate a new recording session - Update this method to use the tabCommunicator
  const initiateRecording = async (): Promise<boolean> => {
    try {
      // Tag all recording errors for Sentry filtering — set early so pre-recording
      // checks (storage, persistence) are also captured under this tag
      SentryService.setTag('recordingType', 'encounter');
      SentryService.setTag('recordingPhase', 'pre_check');

      if (!useRecordingServiceRef.current) {
        if (!webRecordingInstance.current) {
          webRecordingInstance.current = new WebRecordingService();
        }
      }

      if (!recordingStatus.readyToRecord) {
        console.log("Wasn't ready to record, resetting");
        throw new Error('Not ready to record');
      }

      if (useStorageServiceRef.current && storageServiceRef.current) {
        // V2 path: verify StorageService is ready (it handles persistence during init)
        const storageState = storageServiceRef.current.getSnapshot();
        if (storageState.status !== 'ready') {
          disableStreamingPipeline(
            'storage_not_ready',
            new Error(`StorageService not ready at recording start (status: ${storageState.status})`),
          );
          return false;
        } else {
          // StorageService is ready — re-request persistence before recording
          // (Safari may have revoked it, or init request may have been denied)
          const persisted = await storageServiceRef.current.requestPersistence();
          setStoragePersisted(persisted);

          // Log storage quota for Sentry diagnostics
          const estimate = await storageServiceRef.current.getStorageEstimate();
          if (estimate) {
            SentryService.addBreadcrumb({
              category: 'recording',
              message: `Pre-recording storage: ${Math.round(estimate.usageBytes / (1024 * 1024))}MB used / ${Math.round(estimate.quotaBytes / (1024 * 1024))}MB quota, persisted=${persisted}`,
              level: 'info',
            });
          }

          // Check storage quota via StorageService
          if (estimate) {
            const estimatedChunkSizeMB = 5 * (RECORDING_CHUNK_LENGTH_SECONDS / 60);
            const minimumRequiredQuotaMB = estimatedChunkSizeMB * 1;
            const availableBytes = estimate.quotaBytes - estimate.usageBytes;
            const minimumRequiredBytes = minimumRequiredQuotaMB * 1024 * 1024;

            if (availableBytes < minimumRequiredBytes) {
              const availableMB = Math.round(availableBytes / (1024 * 1024));
              const quotaMB = Math.round(estimate.quotaBytes / (1024 * 1024));
              const usagePercent = Math.round(
                (estimate.usageBytes / estimate.quotaBytes) * 100,
              );

              setStorageInfo({ availableMB, quotaMB, usagePercent, minimumRequiredQuotaMB });

              const errorMessage = `Not enough storage space available (${availableMB}MB free of ${quotaMB}MB, ${usagePercent}% used). Please free up storage space and try again.`;
              console.error(errorMessage);

              SentryService.logEvent('Insufficient storage space for recording', {
                level: 'error',
                extra: { availableMB, quotaMB, usagePercent, minimumRequiredQuotaMB },
              });

              enqueueSnackbar(
                <div className="flex flex-col gap-4 lg:flex-row">
                  <div>{errorMessage}</div>
                  <Button
                    onClick={() => setShowStorageCleanupDialog(true)}
                    variant="contained"
                    sx={{
                      flexShrink: 0,
                      maxHeight: '42px',
                      bgcolor: 'background.paper',
                      color: 'error.main',
                      '&:hover': {
                        opacity: 0.9,
                        bgcolor: 'background.paper',
                      },
                      transition: 'opacity 0.2s ease-in-out',
                    }}
                  >
                    Manage Storage
                  </Button>
                </div>,
                { variant: 'error' },
              );
              SentryService.setTag('recordingType', '');
              SentryService.setTag('recordingPhase', '');
              return false;
            }
          }
        }
      }

      // V1 path (or V2 fallback): check IndexedDB storage space and request persistence
      if (!useStorageServiceRef.current) {
        // Define how many chunks we need at minimum
        const minimumChunksRequired = 1; // TODO: move to config. (?)

        // Calculate minimum required quota in MB based on the chunk size formula
        const estimatedChunkSizeMB = 5 * (RECORDING_CHUNK_LENGTH_SECONDS / 60); // ~7.5MB per chunk at 90s
        const minimumRequiredQuotaMB = estimatedChunkSizeMB * minimumChunksRequired;

        // Check if there's enough storage space for required chunks
        const storageCheck = await indexedDBManager.checkStorageSpace(minimumChunksRequired);
        if (!storageCheck.hasEnoughSpace) {
          const availableMB = Math.round(
            (storageCheck.usageDetails.quota! - storageCheck.usageDetails.usage!) / (1024 * 1024),
          );
          const quotaMB = Math.round(storageCheck.usageDetails.quota! / (1024 * 1024));
          const usagePercent = Math.round(
            (storageCheck.usageDetails.usage! / storageCheck.usageDetails.quota!) * 100,
          );

          // Store storage info for the dialog
          setStorageInfo({
            availableMB,
            quotaMB,
            usagePercent,
            minimumRequiredQuotaMB,
          });

          const errorMessage = `Not enough storage space available (${availableMB}MB free of ${quotaMB}MB, ${usagePercent}% used). Please free up storage space and try again.`;
          console.error(errorMessage, storageCheck);

          SentryService.logEvent('Insufficient storage space for recording', {
            level: 'error',
            extra: {
              storageDetails: storageCheck.usageDetails,
              availableMB,
              quotaMB,
              usagePercent,
              minimumRequiredQuotaMB,
            },
          });

          enqueueSnackbar(
            <div className="flex flex-col gap-4 lg:flex-row">
              <div>{errorMessage}</div>
              <Button
                onClick={() => setShowStorageCleanupDialog(true)}
                variant="contained"
                sx={{
                  flexShrink: 0,
                  maxHeight: '42px',
                  bgcolor: 'background.paper',
                  color: 'error.main',
                  '&:hover': {
                    opacity: 0.9,
                    bgcolor: 'background.paper',
                  },
                  transition: 'opacity 0.2s ease-in-out',
                }}
              >
                Manage Storage
              </Button>
            </div>,
            { variant: 'error' },
          );
          SentryService.setTag('recordingType', '');
          SentryService.setTag('recordingPhase', '');
          return false;
        }

        // Request persistent storage permission
        try {
          // Only request if we don't already have persistent storage
          if (storagePersisted !== true) {
            const isPersistent = await indexedDBManager.requestPersistentStorage();
            setStoragePersisted(isPersistent);

            if (isPersistent) {
              console.log('Storage persistence granted!');
              SentryService.addBreadcrumb({
                category: 'storage.persistence',
                message: 'Storage persistence successfully granted',
                level: 'info',
              });
            } else {
              // Just log - we'll continue with recording even if persistence isn't granted
              console.warn(
                'Storage persistence not granted. Data may be cleared by the browser under storage pressure.',
              );
              SentryService.addBreadcrumb({
                category: 'storage.persistence',
                message: 'Storage persistence request denied',
                level: 'warning',
              });
            }
          } else {
            console.log('Storage already persistent, not requesting again');
          }
        } catch (error) {
          // Just log the error - we'll continue with recording even if there's an issue with the persistence request
          console.error('Error requesting storage persistence:', error);
          SentryService.captureException(error, {
            level: 'warning',
            tags: { component: 'RecordingContext' },
            extra: { method: 'initiateRecording' },
          });
        }
      }

      // Reset UploadService refs for new recording
      enqueuedChunkCountRef.current = 0;
      pendingFinalizationRef.current = null;

      // Reset SessionCompletionService state (ensures canGenerateNote is false for new session)
      sessionCompletionServiceRef.current?.reset();

      const startingTimestamp = Date.now();

      SentryService.setTag('recordingPhase', 'initialization');
      setRecordingStatus(prev => ({
        ...prev,
        isStarting: true,
      }));

      const newRecordingSession = new RecordingSession({});
      const recordingId = newRecordingSession.id;

      SentryService.setTag('recordingId', recordingId);

      localStorage.setItem('currentRecordingSessionId', newRecordingSession.id);
      newRecordingSession.startSession();
      newRecordingSession.addLog('DeviceInfo', {
        platform: 'web-recorder',
        version: FRONTEND_RECORDING_APP_VERSION,
      });

      // Add initial media stream info
      newRecordingSession.addMediaStreamInfo({
        useSharedMedia,
        selectedMicrophoneId: selectedMicrophone?.deviceId,
        streamCreatedAt: new Date().toISOString(),
      });

      currentRecordingSession.current = newRecordingSession;

      // Create breadcrumb for transcription session start
      SentryService.addBreadcrumb({
        category: 'audio.session',
        message: 'Starting new transcription session',
        level: 'info',
        data: {
          recordingId,
          useSharedMedia,
          selectedMicrophoneId: selectedMicrophone?.deviceId,
        },
      });

      const newTranscriptionSessionId = await startTranscriptionSession(
        newRecordingSession.id,
        await getAccessToken(),
        useRecordingServiceRef.current ? 'streaming' : 'classic',
      );

      if (!newTranscriptionSessionId) {
        console.error("Couldn't get a new transcription session id.");
        SentryService.addBreadcrumb({
          category: 'audio.session',
          message: 'Failed to get transcription session ID',
          level: 'error',
        });
        throw new Error('Failed to get transcription session ID');
      }

      // Reset elapsed time if new transcription session
      elapsedTime.current = 0;
      // Store actual recording start time
      recordingStartTime.current = Date.now();

      setCurrentTranscriptionSessionId(newTranscriptionSessionId);
      // Also store in ref as fallback for stale closure issues (see ref declaration for details)
      transcriptionSessionIdRef.current = newTranscriptionSessionId;
      newRecordingSession.associateTranscriptionSession(newTranscriptionSessionId);

      // Set transcription session ID as a tag for tracing
      SentryService.setTag('transcriptionSessionId', newTranscriptionSessionId);
      SentryService.setTransactionAttribute('transcriptionSessionId', newTranscriptionSessionId);

      // Acquire stream via AudioInputDeviceService when gated
      let preAcquiredStream: MediaStream | undefined;
      if (useAudioDeviceService && audioDeviceServiceRef.current?.isInitialized()) {
        try {
          const service = audioDeviceServiceRef.current;
          const existingStream = service.getActiveStream();
          const devices = service.getDevices();
          const target = selectedMicrophone
            ? devices.find(d => d.browserDeviceId === selectedMicrophone.deviceId)
            : undefined;

          // Check audio health state — a stream can report active/live tracks
          // while producing zero audio (e.g. after Bluetooth disconnect, OS
          // interruption, or Chrome resource reclaim). The health monitor
          // detects this via AnalyserNode RMS analysis.
          const healthState = service.getHealthState();
          const isHealthy = healthState === 'healthy' || healthState === 'initializing';

          if (
            existingStream &&
            existingStream.stream.active &&
            existingStream.stream.getAudioTracks().some(t => t.readyState === 'live') &&
            (!target || existingStream.device.internalId === target.internalId) &&
            isHealthy
          ) {
            // Reuse the pre-acquired stream (from health monitoring)
            preAcquiredStream = existingStream.stream;
            newRecordingSession.addLog('AudioServiceReusedPreAcquired', {
              deviceId: existingStream.device.browserDeviceId,
              transport: existingStream.device.transport,
              healthState,
            });
          } else {
            // Different device, stale stream, or unhealthy audio — acquire fresh
            if (!isHealthy && existingStream) {
              newRecordingSession.addLog('AudioServiceStaleStreamRejected', {
                healthState,
                streamActive: existingStream.stream.active,
                trackReadyState: existingStream.stream.getAudioTracks()[0]?.readyState,
              });
            }
            const acquireResult = await service.acquire(target?.internalId);
            preAcquiredStream = acquireResult.stream;

            if (acquireResult.wasSubstituted) {
              audioDeviceTracking.substituted({
                requested_transport: target?.transport,
                actual_transport: acquireResult.device.transport,
              });
              setSelectedMicrophone(normalizedToMediaDeviceInfo(acquireResult.device));

              // Notify user about the device switch
              const requestedLabel = selectedMicrophone?.label || 'Selected microphone';
              const actualLabel = acquireResult.device.displayLabel || 'default microphone';
              enqueueSnackbar(
                `${requestedLabel} unavailable. Recording with ${actualLabel}.`,
                { variant: 'warning' },
              );
            }

            newRecordingSession.addLog('AudioServiceAcquired', {
              deviceId: acquireResult.device.browserDeviceId,
              wasSubstituted: acquireResult.wasSubstituted,
              transport: acquireResult.device.transport,
            });
          }
        } catch (error) {
          console.error('AudioInputDeviceService acquire failed, falling back', error);
          SentryService.captureException(
            error instanceof Error ? error : new Error(String(error)),
            {
              tags: { component: 'RecordingContext', method: 'initiateRecording' },
            },
          );
          // preAcquiredStream stays undefined → createMediaRecorder uses own getUserMedia
        }
      }

      if (useRecordingServiceRef.current && recordingServiceRef.current) {
        // V4 path: RecordingService handles MediaRecorder lifecycle internally
        if (!preAcquiredStream) {
          // Fallback: acquire stream directly when AudioInputDeviceService is not active
          preAcquiredStream = await navigator.mediaDevices.getUserMedia({
            audio: selectedMicrophone?.deviceId
              ? { deviceId: { exact: selectedMicrophone.deviceId } }
              : true,
          });
        }

        // Telehealth/headphones: mix mic + system audio if shared media is enabled
        const mixerResult = await acquireRecordingStream(preAcquiredStream, {
          useSharedMedia,
          preferCurrentTab: isLoadedInChromeExtension.current,
        });
        streamCleanupRef.current = mixerResult.cleanup;
        mixerResultRef.current = mixerResult;

        // Phase 3c/4c: Wire telehealth remote audio into mixer (only when session configured)
        if (dailyCallServiceRef.current && telehealthConfigRef.current) {
          const rawMicTrack = preAcquiredStream.getAudioTracks()[0];

          // 1. Clone mic for Daily (recording pipeline gets original)
          const deviceBridge = new DeviceBridgeService(dailyCallServiceRef.current);
          deviceBridgeRef.current = deviceBridge;
          const dailyMicClone = deviceBridge.createInitialClone(rawMicTrack);

          // 2. Push cloned mic to Daily
          if (dailyCallServiceRef.current.getCallObject()) {
            // Phase 4c: call already initialized at join-session time
            await dailyCallServiceRef.current.setAudioDevice(dailyMicClone);
          } else {
            // Legacy: initialize at recording start
            dailyCallServiceRef.current.initialize({ audioSource: dailyMicClone });
          }

          // 3. Subscribe to remote track events
          dailyEventCleanupRef.current = wireDailyTrackEvents(
            dailyCallServiceRef.current,
            mixerResultRef,
          );

          // 4. Join if not yet connected
          const currentState = dailyCallServiceRef.current.getSnapshot();
          if (currentState.status !== 'connected' && currentState.status !== 'joining') {
            dailyCallServiceRef.current.join(
              telehealthConfigRef.current.roomUrl,
              telehealthConfigRef.current.token,
            ).catch((err) => {
              SentryService.captureException(err instanceof Error ? err : new Error(String(err)));
              SentryService.addBreadcrumb({
                category: 'telehealth.mixer',
                message: `Daily join failed: ${err}`,
                level: 'error',
              });
            });
          }

          // 5. Sync any existing remote tracks (late-join / already-connected scenario)
          const existingRemoteTracks = dailyCallServiceRef.current.getRemoteAudioTracks();
          existingRemoteTracks.forEach((track, participantId) => {
            mixerResultRef.current?.connectRemoteTrack(participantId, track);
          });

          SentryService.setTag('telehealth_active', 'true');
        }

        // Reset headphones toggle if shared media wasn't actually acquired
        // (user cancelled display picker or no audio track available)
        if (useSharedMedia && !mixerResult.sharedMediaActive) {
          setUseSharedMedia(false);
        }

        const deviceId = preAcquiredStream.getAudioTracks()[0]?.getSettings()?.deviceId;
        await recordingServiceRef.current.start(mixerResult.stream, newTranscriptionSessionId, {
          deviceId,
          mixerAudioContext: mixerResult.audioContext ?? undefined,
          rawMicStream: preAcquiredStream,
        });

        // V5: create session manifest for crash recovery tracking
        if (useSessionCompletionServiceRef.current && sessionManifestRef.current) {
          const mimeType = recordingServiceRef.current.getSnapshot().mimeType ?? 'audio/webm;codecs=opus';
          sessionManifestRef.current.create(newTranscriptionSessionId, mimeType, recordingEncounterIdRef.current ?? undefined).catch((error) => {
            SentryService.addBreadcrumb({
              category: 'session.manifest',
              message: `Failed to create manifest: ${error}`,
              level: 'warning',
            });
          });
        }
      } else {
        const mediaRecorderCreated = await createMediaRecorder(
          newTranscriptionSessionId,
          preAcquiredStream,
        );
        if (!mediaRecorderCreated) {
          console.error('Failed to create media recorder.');
          throw new Error('Failed to create media recorder');
        }
      }

      // Claim ownership of this recording session
      console.log('Claiming ownership of recording session');
      localStorage.setItem('recording_starter_id', tabCommunicator.getTabId());
      setAllowRecorderControl(true);

      SentryService.setTag('recordingPhase', 'active');
      setRecordingStatus(prev => {
        const newStatus = {
          ...prev,
          isStarting: false,
          isRecording: true,
          readyToRecord: false,
          readyToSubmit: true,
          recordingPaused: false,
        };
        sendMessageToExtension({ type: 'recordingStatus', data: newStatus });
        return newStatus;
      });

      // Analytics: recording successfully started (both pipelines)
      recordingTracking.start({
        session_id: newTranscriptionSessionId,
        recording_pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
        encounter_id: recordingEncounterIdRef.current ?? undefined,
      });
      recordingTracking.startedSuccessfully({
        recording_pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
        time_in_starting_ms: String(Date.now() - startingTimestamp),
      });

      // Start timer and track recording metrics
      startTimer();

      // Ensure audio history updates are not paused
      setPaused(false);

      return true;
    } catch (error) {
      console.error('Error initiating recording:', error);

      // CRITICAL: Clean up recording mechanism to prevent stale callbacks
      if (useRecordingServiceRef.current && recordingServiceRef.current) {
        try {
          await recordingServiceRef.current.stop();
        } catch {
          // Best-effort — service may already be in error/idle state
        }
        streamCleanupRef.current?.();
        streamCleanupRef.current = null;
        mixerResultRef.current = null;
        // Phase 3c: Clean up telehealth resources on init failure
        dailyEventCleanupRef.current?.();
        dailyEventCleanupRef.current = null;
        deviceBridgeRef.current?.dispose();
        deviceBridgeRef.current = null;
        // Don't destroy dailyCallService — let caller decide
      } else if (webRecordingInstance.current) {
        console.log('Cleaning up failed media recorder instance');
        webRecordingInstance.current.stopMediaStreamRecording();
        webRecordingInstance.current = undefined;
      }

      // Clear any partial session state
      if (
        currentRecordingSession.current &&
        !currentRecordingSession.current.transcription_session_id
      ) {
        console.log('Cleaning up partial recording session');
        currentRecordingSession.current = undefined;
      }

      // Track recording start failure for Statsig observability
      const errorMessage = error instanceof Error ? error.message : String(error);
      recordingTracking.startFailed({
        session_id: transcriptionSessionIdRef.current || undefined,
        error_type: classifyStartError(error),
        error_message: errorMessage.slice(0, 200),
        recording_pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
        had_api_session: !!transcriptionSessionIdRef.current,
        encounter_id: recordingEncounterIdRef.current ?? undefined,
      });

      SentryService.logEvent('Error initiating recording.', {
        level: 'error',
        extra: { error },
      });
      enqueueSnackbar('Failed to start recording. Check audio settings and try again.', {
        variant: 'error',
      });
      await cleanUpSession();
      return false;
    }
  };

  // Clean up the recording session
  const cleanUpSession = async () => {
    console.log('Cleaning up session');

    // Release shared media resources (telehealth/headphones mode)
    streamCleanupRef.current?.();
    streamCleanupRef.current = null;
    mixerResultRef.current = null;

    // Phase 3c: Clean up telehealth event subscriptions (recording stops, call may continue)
    dailyEventCleanupRef.current?.();
    dailyEventCleanupRef.current = null;
    // Do NOT call dailyCallService.leave()/destroy() — call continues after recording stops
    // Do NOT clear dailyCallServiceRef — service outlives recording

    // NOTE: We intentionally do NOT release the AudioInputDeviceService stream here.
    // The stream stays alive so the level meter continues showing mic activity between
    // recordings (waiting room pattern). Release only happens on page navigation,
    // tab visibility change, or unmount.

    // Abort any in-flight uploads and reset UploadService state
    if (useUploadServiceRef.current && uploadServiceRef.current) {
      uploadServiceRef.current.abortAll();
      pendingFinalizationRef.current = null;
      enqueuedChunkCountRef.current = 0;
    }

    // Clear chunk storage and session manifest
    if (useStorageServiceRef.current && storageServiceRef.current) {
      const sessionId = currentRecordingSession.current?.transcription_session_id;
      if (sessionId) {
        await storageServiceRef.current.deleteSession(sessionId);
        // Delete manifest to prevent false recovery on reload (e.g. short recordings)
        await sessionManifestRef.current?.delete(sessionId);
      }
    } else {
      await indexedDBManager.clearAll();
    }
    pendingChunkBlobsRef.current.clear();
    currentRecordingSession.current?.addLog('cleaning up session');

    resetTimer();

    // Clear recording starter ID from localStorage AFTER all other cleanup is done
    localStorage.removeItem('recording_starter_id');
    localStorage.removeItem('currentRecordingSessionId');
    localStorage.removeItem('isLeaving');

    // return recording status state
    setRecordingStatus(prevStatus => ({
      ...prevStatus,
      isRecording: false,
      readyToRecord: true,
      readyToSubmit: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
      recordingPaused: false,
      isReuploading: false,
    }));

    setLowDataMode(false);
    setChunkLength(RECORDING_CHUNK_LENGTH_SECONDS);

    isRecordingEnding.current = false;
    currentRecordingSession.current = undefined;
    // Clear the transcription session ID ref AND localStorage-backed state
    // to prevent stale session IDs from bleeding into the next encounter
    transcriptionSessionIdRef.current = '';
    setCurrentTranscriptionSessionId('');

    setNoteInformation({
      templateId: templatesList ? templatesList[0]?.template_id : '',
    });

    // clear audio visualizers
    setAudioData(-1);

    // Clear recording Sentry tags to prevent pollution on non-recording errors
    SentryService.setTag('recordingType', '');
    SentryService.setTag('recordingId', '');
    SentryService.setTag('recordingPhase', '');
    SentryService.setTag('transcriptionSessionId', '');

    // Observability: capture expected final state after cleanup
    const finalStatus = {
      ...recordingStatus,
      isRecording: false,
      readyToRecord: true,
      readyToSubmit: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
    };
    recordingTracking.cleanupCompleted({
      cleanup_type: 'full',
      recording_pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
      final_status_snapshot: JSON.stringify(finalStatus),
      recording_paused_reset: String(finalStatus.recordingPaused === false),
      is_reuploading_reset: String(finalStatus.isReuploading === false),
    });
  };

  // Soft reset for Save & Exit navigation — resets UI/localStorage state so the next
  // encounter starts clean, WITHOUT aborting V5 background drain or deleting storage.
  const softResetForNavigation = () => {
    resetTimer();

    localStorage.removeItem('recording_starter_id');
    localStorage.removeItem('currentRecordingSessionId');
    localStorage.removeItem('isLeaving');

    setRecordingStatus(prevStatus => ({
      ...prevStatus,
      isRecording: false,
      readyToRecord: true,
      readyToSubmit: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
      recordingPaused: false,
      isReuploading: false,
    }));

    setLowDataMode(false);
    setChunkLength(RECORDING_CHUNK_LENGTH_SECONDS);

    isRecordingEnding.current = false;
    currentRecordingSession.current = undefined;
    // Clear the transcription session ID ref AND localStorage-backed state
    // to prevent stale session IDs from bleeding into the next encounter
    transcriptionSessionIdRef.current = '';
    setCurrentTranscriptionSessionId('');

    setAudioData(-1);

    SentryService.setTag('recordingType', '');
    SentryService.setTag('recordingId', '');
    SentryService.setTag('recordingPhase', '');
    SentryService.setTag('transcriptionSessionId', '');

    // Observability: capture expected final state after soft reset
    const finalStatus = {
      ...recordingStatus,
      isRecording: false,
      readyToRecord: true,
      readyToSubmit: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
    };
    recordingTracking.cleanupCompleted({
      cleanup_type: 'soft_navigation',
      recording_pipeline: useRecordingServiceRef.current ? 'streaming' : 'classic',
      final_status_snapshot: JSON.stringify(finalStatus),
      recording_paused_reset: String(finalStatus.recordingPaused === false),
      is_reuploading_reset: String(finalStatus.isReuploading === false),
    });
  };

  // Lightweight reset for crash/dead-button recovery — only touches persisted state.
  // Does NOT tear down services, streams, or timers (none exist after a crash).
  const resetRecordingState = useCallback(() => {
    setRecordingStatus({
      isRecording: false,
      isReuploading: false,
      isStopping: false,
      isStarting: false,
      chunksToBeUploaded: false,
      recordingPaused: false,
      readyToRecord: true,
      readyToSubmit: false,
    });
    localStorage.removeItem('recording_starter_id');
    localStorage.removeItem('currentRecordingSessionId');
    localStorage.removeItem('isLeaving');
    localStorage.removeItem('elapsedTime');
    // Clear stale transcription session ID to prevent cross-encounter bleed
    transcriptionSessionIdRef.current = '';
    setCurrentTranscriptionSessionId('');
  }, [setRecordingStatus, setCurrentTranscriptionSessionId]);

  // Helper function to delete a recording session
  const deleteRecordingHelper = async (): Promise<boolean> => {
    try {
      // Attempt to delete the transcription session
      const response = await APIService.makeAPIPostRequest({
        requestString: '/transcriptionSession/deleteTranscriptionSession',
        accessToken: await getAccessToken(),
        body: { transcription_session_id: currentTranscriptionSessionId },
      });

      if (!response.ok) {
        throw new Error('Failed to delete transcription session.');
      }

      // Clean up recording starter ID
      localStorage.removeItem('recording_starter_id');

      await cleanUpSession(); // call cleanup

      return true;
    } catch (error) {
      console.error('Error deleting recording:', error);
      SentryService.logEvent('Error deleting recording.', {
        level: 'error',
        extra: { error, transcriptionSessionId: currentTranscriptionSessionId },
      });
      return false;
    }
  };

  // Function to send the recording
  const sendRecording = async (): Promise<{ note_id: string; thread_id?: string } | undefined> => {
    if (!currentTranscriptionSessionId) {
      console.error('No transcription session to submit.');
      return undefined;
    }
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        console.error('No access token available.');
        return undefined;
      }
      if (loadedInChromeExtension) {
        APIService.changeAppVersion('chrome', '2.0.0');
      }
      const startNoteProcessingSessionResponse = await startNoteProcessingSession(
        currentTranscriptionSessionId,
        noteInformation,
        accessToken,
      );

      console.log('Note Processing Session Started.');

      // show success dialog
      enqueueSnackbar('Your note has been successfully submitted!', {
        variant: 'success',
      });

      // Clean up recording starter ID
      localStorage.removeItem('recording_starter_id');

      await cleanUpSession(); // call cleanup

      return {
        note_id: startNoteProcessingSessionResponse.note_id,
        thread_id: startNoteProcessingSessionResponse.thread_id,
      };
    } catch (response: any) {
      let snackBarText =
        'Your recording could not be submitted at this time, please try a different template or contact support.';

      if (response.error_code === 'INVALID_TEMPLATE') {
        snackBarText =
          'The template you selected does not contain any sections. Please add a section to it or try a different template.';
      }

      console.error('Could not start Note Processing Session');
      SentryService.logEvent('Error submitting recording.', {
        level: 'error',
        extra: {
          error: response.error,
          transcriptionSessionId: currentTranscriptionSessionId,
        },
      });

      enqueueSnackbar(snackBarText, { variant: 'error' });

      // return recording status state
      setRecordingStatus(prevStatus => ({
        ...prevStatus,
        readyToSubmit: true,
      }));
      return undefined;
    }
  };

  const attemptRecoverRecordingSession = async () => {
    console.log('Attempting to recover a recording session.');
    const recoveredRecordingSessionId = localStorage.getItem('currentRecordingSessionId');
    if (recoveredRecordingSessionId) {
      const recoveredRecordingSession = await getRecordingSessionFromId(
        recoveredRecordingSessionId,
      );
      if (recoveredRecordingSession) {
        console.log(`Successfully recovered recording session ${recoveredRecordingSession.id}`);
        currentRecordingSession.current = recoveredRecordingSession;
      }
    }
  };

  const getRecordingSessionFromId = async (
    recordingSessionId: string,
  ): Promise<RecordingSession | null> => {
    if (recordingSessionId) {
      const recordingSession = await RecordingSession.loadFromStorage(recordingSessionId);
      if (recordingSession) {
        return recordingSession;
      }
    }

    return null;
  };

  // handle reload case
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Only the recording owner should show the "Leave site?" dialog.
      // Non-owner tabs close/reload silently — no dialog, no state mutation.
      if (recordingStatus.isRecording && allowRecorderControl) {
        // Do NOT pause the recording or mutate state.
        // If user stays: recording continues seamlessly.
        // If user leaves: Web Lock auto-releases, SessionRecoveryService handles recovery.
        event.preventDefault();
      }

      // Track session abandonment if mid-completion (draining or offline_pending)
      if (useSessionCompletionServiceRef.current && sessionCompletionServiceRef.current) {
        const completionState = sessionCompletionServiceRef.current.getSnapshot();
        if (completionState.status === 'draining' || completionState.status === 'offline_pending') {
          const client = getStatsigClientInstance();
          if (client) {
            client.logEvent(EventNames.SESSION_ABANDONED, undefined, {
              session_id: transcriptionSessionIdRef.current || '',
              completion_status: completionState.status,
              drain_elapsed_seconds: String(completionState.drainElapsedSeconds),
              total_chunks: String(completionState.totalChunks),
              uploaded_chunks: String(completionState.uploadedChunks),
              failed_chunks: String(completionState.failedChunks),
            });
            // Best-effort flush — may not complete before page unloads
            client.flush?.();
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [recordingStatus.isRecording, allowRecorderControl]);

  // Track page unload during active drain — fires synchronously so Statsig SDK
  // picks it up in its own sendBeacon flush on visibilitychange/pagehide.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;

      const completionService = sessionCompletionServiceRef.current;
      if (!completionService) return;

      const snapshot = completionService.getSnapshot();
      if (snapshot.status !== 'draining' && snapshot.status !== 'offline_pending') return;

      const uploadSnapshot = uploadServiceRef.current?.getSnapshot();

      sessionCompletionTracking.pageUnload({
        session_id: transcriptionSessionIdRef.current || undefined,
        completion_status: snapshot.status,
        drain_elapsed_seconds: snapshot.drainElapsedSeconds,
        total_chunks: snapshot.totalChunks,
        uploaded_chunks: snapshot.uploadedChunks,
        failed_chunks: snapshot.failedChunks,
        pending_queue_depth: uploadSnapshot?.queueDepth,
        in_flight_count: uploadSnapshot?.inFlightCount,
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [sessionCompletionTracking]);

  // Initialize elapsed time and currentRecordingSession from localStorage on component mount
  useEffect(() => {
    const storedElapsedTime = localStorage.getItem('elapsedTime');
    if (storedElapsedTime) {
      elapsedTime.current = parseInt(storedElapsedTime, 10);
    }

    attemptRecoverRecordingSession();
  }, []);

  // Update localStorage when elapsed time changes
  useEffect(() => {
    if (recordingStatus.isRecording) {
      const minutes = Math.floor(elapsedTime.current / 60);
      const seconds = elapsedTime.current % 60;
      const formattedTime = `${minutes}:${String(seconds).padStart(2, '0')}`;
      sendMessageToExtension({ type: 'recordingTime', data: formattedTime });
      localStorage.setItem('elapsedTime', elapsedTime.current.toString());
      if (elapsedTime.current >= MAX_RECORDING_LENGTH_SECONDS) {
        SentryService.logEvent('Max recording length exceeded.', {
          level: 'info',
          extra: {
            elapsedTime: elapsedTime.current,
            transcriptionSessionId: currentTranscriptionSessionId,
          },
        });
        initiateEndRecording();
      }
    }
  }, [elapsedTime, recordingStatus.isRecording]);

  // Initiate a WebRecordingService instance on load
  useEffect(() => {
    if (!webRecordingInstance.current) {
      webRecordingInstance.current = new WebRecordingService();
    }
  }, []);

  // Set default template
  useEffect(() => {
    if (templatesList && templatesList.length > 0 && !noteInformation.templateId) {
      setNoteInformation(prev => ({
        ...prev,
        templateId: templatesList[0].template_id,
      }));
    }
  }, [templatesList]);

  // Manage timer based on recording status
  useEffect(() => {
    if (recordingStatus.isRecording && !recordingStatus.recordingPaused) {
      startTimer();
    } else {
      stopTimer();
    }

    // Cleanup timer on component unmount
    return () => {
      stopTimer();
    };
  }, [recordingStatus.isRecording, recordingStatus.recordingPaused]);

  useEffect(() => {
    console.log('Updating loadedInChromeExtension', loadedInChromeExtension);
    isLoadedInChromeExtension.current = loadedInChromeExtension;
  }, [loadedInChromeExtension]);

  // Get accurate recording times based on actual start time
  const getRecordingTimes = () => {
    if (!recordingStartTime.current) {
      return null;
    }

    return {
      startTime: new Date(recordingStartTime.current),
      endTime: new Date(), // Use actual current time, not calculated
      elapsedTime: elapsedTime.current,
    };
  };

  // Update checkRecordingOwnership to track previous value of allowRecorderControl
  const prevAllowRecorderControl = useRef(allowRecorderControl);

  // Define checkRecordingOwnership before any usage
  const checkRecordingOwnership = useCallback(() => {
    // Get the current recording starter ID
    const currentStarterId = localStorage.getItem('recording_starter_id');

    if (currentStarterId) {
      const currentTabId = tabCommunicator.getTabId();
      const isRecordingStarter = currentStarterId === currentTabId;

      // Only update if the value is changing
      if (isRecordingStarter !== prevAllowRecorderControl.current) {
        setAllowRecorderControl(isRecordingStarter);
        prevAllowRecorderControl.current = isRecordingStarter;
      }
    } else {
      // If there's no active session, allow this tab to control the recording
      if (!prevAllowRecorderControl.current) {
        setAllowRecorderControl(true);
        prevAllowRecorderControl.current = true;
      }
    }
  }, []);

  // Update the visibility change listener
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab became visible, checking recording status');
        checkRecordingOwnership();
      }
    };

    // Add event listener for visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial check when component mounts
    checkRecordingOwnership();

    // Clean up event listener on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkRecordingOwnership]);

  // Visibility-aware stream suspension: release mic stream when tab goes to background
  // (avoids holding a single-consumer resource in inactive tabs), re-acquire when visible.
  // Never interrupts an active recording.
  useEffect(() => {
    if (!useAudioDeviceServiceRef.current) return;

    const handleStreamVisibility = () => {
      const service = audioDeviceServiceRef.current;
      if (!service?.isInitialized()) return;

      if (document.visibilityState === 'hidden') {
        // Tab hidden — release stream if not recording
        if (!isRecordingRef.current && service.getActiveStream()) {
          service.release();
          setAudioHealthState('idle');
        }
      } else if (document.visibilityState === 'visible') {
        // Tab visible — re-acquire if stream was suspended
        if (!isRecordingRef.current && !service.getActiveStream()) {
          const savedMicId = localStorage.getItem('selectedMicrophoneId');
          const devices = service.getDevices();
          const target = savedMicId
            ? devices.find(d => d.browserDeviceId === savedMicId)
            : undefined;

          service.acquire(target?.internalId).catch(error => {
            console.warn('Stream re-acquire on tab focus failed (non-fatal)', error);
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleStreamVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleStreamVisibility);
    };
  }, []);

  // Hook to monitor localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Handle recording_starter_id changes
      if (event.key === 'recording_starter_id') {
        const oldStarterId = event.oldValue;
        const newStarterId = event.newValue;
        const currentTabId = tabCommunicator.getTabId();

        // if current tab was the recording starter, and it's no longer the recording starter,
        // then we should stop the recording (both classic and streaming pipelines)
        if (oldStarterId === currentTabId && newStarterId !== currentTabId) {
          console.log('Stopping recording because tab changed');
          if (webRecordingInstance.current) {
            webRecordingInstance.current.stopMediaStreamRecording();
          }
          if (recordingServiceRef.current) {
            recordingServiceRef.current.stop().then(() => {
              // Clean up mixer/stream resources after stop completes
              streamCleanupRef.current?.();
              streamCleanupRef.current = null;
              mixerResultRef.current = null;
            }).catch(err => {
              console.warn('Error stopping RecordingService on tab ownership loss:', err);
            });
          }
        }

        // Check ownership when recording_starter_id changes
        checkRecordingOwnership();
      }

      // Handle recordingStatus changes from other tabs
      // This is the simple cross-tab sync - when Tab 1 updates localStorage, Tab 2 gets notified
      if (event.key === 'recordingStatus' && event.newValue) {
        try {
          const newStatus = JSON.parse(event.newValue);
          console.debug('Recording status updated from another tab:', newStatus);
          setRecordingStatus(newStatus);

          // If recording stopped and we're not the owner, clear stale recording_starter_id
          if (!newStatus.isRecording) {
            const currentStarterId = localStorage.getItem('recording_starter_id');
            const currentTabId = tabCommunicator.getTabId();
            if (currentStarterId && currentStarterId !== currentTabId) {
              console.debug('Clearing stale recording_starter_id after remote stop');
              localStorage.removeItem('recording_starter_id');
            }
            // Re-check ownership now that recording has stopped
            checkRecordingOwnership();
          }
        } catch (e) {
          console.error('Error parsing recordingStatus from storage event', e);
        }
      }
    };

    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkRecordingOwnership, setRecordingStatus]);

  // Add effect to call rehydrateStateFromLocalStorage when allowRecorderControl becomes true
  useEffect(() => {
    if (allowRecorderControl) {
      console.log('allowRecorderControl became true, rehydrating state');
      rehydrateStateFromLocalStorage();
    } else if (!allowRecorderControl) {
      console.log('Lost recording control, stopping recording pipelines');
      if (webRecordingInstance.current) {
        webRecordingInstance.current.stopMediaStreamRecording();
      }
      if (recordingServiceRef.current) {
        recordingServiceRef.current.stop().catch(err => {
          console.warn('Error stopping RecordingService on control loss:', err);
        });
      }
    }
  }, [allowRecorderControl, rehydrateStateFromLocalStorage]);

  // Update takeOverRecording to use the new check function
  const takeOverRecording = async (): Promise<boolean> => {
    try {
      console.log('Taking over recording to this tab');

      SentryService.logEvent('RecordingTabTakeover', {
        level: 'warning',
        extra: {
          currentTabId: tabCommunicator.getTabId(),
          previousTabId: localStorage.getItem('recording_starter_id'),
          recordingStatus: recordingStatus,
          allowRecorderControl: allowRecorderControl,
        },
      });

      // Set recording_starter_id in localStorage first to claim ownership
      localStorage.setItem('recording_starter_id', tabCommunicator.getTabId());

      // First set the local state - do this immediately
      setAllowRecorderControl(true);
      prevAllowRecorderControl.current = true;

      // Log the current state after taking over
      console.log('Recording ownership claimed by tab:', tabCommunicator.getTabId());
      console.log('Current recording status:', recordingStatus);

      // Force rehydration of state from localStorage
      rehydrateStateFromLocalStorage();

      // If the recorder isn't set up yet (e.g. on reload)
      // and recording is paused, then create a new one and resume
      if (recordingStatus.recordingPaused) {
        if (useRecordingServiceRef.current && recordingServiceRef.current) {
          // V4 path: cold-resume via RecordingService
          const serviceStatus = recordingServiceRef.current.getSnapshot().status;
          if (serviceStatus !== 'idle' && serviceStatus !== 'paused') {
            // RecordingService already has an active recorder — nothing to do
          } else {
            try {
              // Acquire stream
              let stream: MediaStream;
              const activeStream = audioDeviceServiceRef.current?.getActiveStream();
              if (activeStream) {
                stream = activeStream.stream;
              } else {
                const deviceId = selectedMicrophone?.deviceId;
                stream = await navigator.mediaDevices.getUserMedia({
                  audio: deviceId ? { deviceId: { exact: deviceId } } : true,
                });
              }

              // Determine next chunk index from storage
              const sessionId = transcriptionSessionIdRef.current
                || localStorage.getItem('current_transcription_session_id')
                || '';
              const chunks = await storageServiceRef.current?.listChunks(sessionId) ?? [];
              const positiveChunks = chunks.filter(i => i >= 0);
              const nextChunkIndex = positiveChunks.length > 0
                ? Math.max(...positiveChunks) + 1
                : 0;

              await recordingServiceRef.current.resumeFromColdStart(stream, sessionId, nextChunkIndex);
            } catch (error) {
              console.error('Failed to cold-resume via RecordingService:', error);
              SentryService.captureException(error, {
                level: 'error',
                tags: { component: 'RecordingContext', method: 'takeOverRecording' },
              });
              return false;
            }
          }
        } else if (!webRecordingInstance.current?.recorder) {
          // V1 legacy path
          console.log('Recording is paused, setting up recorder to resume');
          const mediaRecorderCreated = await createMediaRecorder();
          if (!mediaRecorderCreated) {
            console.error('Failed to create media recorder.');
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      console.error('Error taking over recording:', error);
      SentryService.logEvent('Error taking over recording.', {
        level: 'error',
        extra: { error },
      });
      return false;
    }
  };

  // Request remote stop via BroadcastChannel
  // Check ownership at runtime using localStorage to avoid stale closure issues
  const requestRemoteStop = useCallback(async (): Promise<{
    success: boolean;
    reason?: string;
  }> => {
    // Check ownership at runtime by reading directly from localStorage
    const currentStarterId = localStorage.getItem('recording_starter_id');
    const currentTabId = tabCommunicator.getTabId();
    const isOwner = !currentStarterId || currentStarterId === currentTabId;

    // If this tab has control, stop directly using the ref to avoid stale closure
    if (isOwner) {
      const result = await performStopRecordingRef.current();
      return { success: result };
    }

    // Otherwise, request remote stop via BroadcastChannel
    setRemoteStopPending(true);
    try {
      const result = await tabCommunicator.broadcastStopRequest();
      return result;
    } finally {
      setRemoteStopPending(false);
    }
  }, []);

  const requestRemotePause = useCallback(async (): Promise<{
    success: boolean;
    reason?: string;
  }> => {
    // Check ownership at runtime by reading directly from localStorage
    const currentStarterId = localStorage.getItem('recording_starter_id');
    const currentTabId = tabCommunicator.getTabId();
    const isOwner = !currentStarterId || currentStarterId === currentTabId;

    // If this tab has control, pause directly using the ref to avoid stale closure
    if (isOwner) {
      const result = performPauseRecordingRef.current();
      return { success: result };
    }

    // Otherwise, request remote pause via BroadcastChannel
    try {
      const result = await tabCommunicator.broadcastPauseRequest();
      return result;
    } catch (error) {
      return { success: false, reason: error instanceof Error ? error.message : String(error) };
    }
  }, []);

  const requestRemoteResume = useCallback(async (): Promise<{
    success: boolean;
    reason?: string;
  }> => {
    // Check ownership at runtime by reading directly from localStorage
    const currentStarterId = localStorage.getItem('recording_starter_id');
    const currentTabId = tabCommunicator.getTabId();
    const isOwner = !currentStarterId || currentStarterId === currentTabId;

    // If this tab has control, resume directly using the ref to avoid stale closure
    if (isOwner) {
      const result = await performResumeRecordingRef.current();
      return { success: result };
    }

    // Otherwise, request remote resume via BroadcastChannel
    try {
      const result = await tabCommunicator.broadcastResumeRequest();
      return result;
    } catch (error) {
      return { success: false, reason: error instanceof Error ? error.message : String(error) };
    }
  }, []);

  /**
   * Checks if storage is persisted and updates the state
   */
  const checkStoragePersistence = useCallback(async (): Promise<boolean> => {
    try {
      const isPersisted = await indexedDBManager.isStoragePersisted();
      setStoragePersisted(isPersisted);
      return isPersisted;
    } catch (error) {
      console.error('Error checking storage persistence:', error);
      setStoragePersisted(false);
      return false;
    }
  }, []);

  // Check storage persistence status on mount
  useEffect(() => {
    // Check for storage persistence when the component mounts
    const checkPersistenceOnMount = async () => {
      try {
        // Check if the browser supports the persistence API and we're in a secure context
        const isPersistenceSupported =
          typeof navigator !== 'undefined' &&
          navigator.storage &&
          typeof navigator.storage.persist === 'function' &&
          window.isSecureContext;

        if (!isPersistenceSupported) {
          console.log('Storage persistence API not supported in this browser');
          setStoragePersisted(false);
          return;
        }

        const isPersisted = await indexedDBManager.isStoragePersisted();
        setStoragePersisted(isPersisted);

        console.log('Storage persistence status:', isPersisted ? 'persistent' : 'not persistent');

        SentryService.addBreadcrumb({
          category: 'storage.persistence',
          message: `Initial storage persistence status: ${isPersisted ? 'persistent' : 'not persistent'}`,
          level: isPersisted ? 'info' : 'warning',
        });
      } catch (error) {
        console.error('Error checking storage persistence on mount:', error);
        setStoragePersisted(false);
      }
    };

    checkPersistenceOnMount();
  }, []);

  // Memoize the context value
  const value = useMemo(
    () => ({
      currentTranscriptionSessionId,
      recordingStatus,
      noteInformation,
      setNoteInformation,
      setUseSharedMedia,
      useSharedMedia,
      noAudioStatus,
      selectedMicrophone,
      availableMicrophones,
      microphonePermissionGranted,
      checkMicrophonePermission,
      setSelectedMicrophone,
      setNoAudioStatus,
      getAvailableMicrophones,
      initiateRecording,
      initiateEndRecording,
      sendRecording,
      pauseRecording,
      resumeRecording,
      deleteRecordingHelper,
      startTimer,
      stopTimer,
      resetTimer,
      elapsedTime: elapsedTime.current,
      appointmentType,
      setAppointmentType,
      cleanUpSession,
      softResetForNavigation,
      releaseMicrophone,
      initiateReupload,
      lowDataMode,
      sendMessageToExtension,
      setShouldBeCleanedUp,
      takeOverRecording,
      allowRecorderControl,
      rehydrateStateFromLocalStorage,
      checkRecordingOwnership,
      storagePersisted,
      checkStoragePersistence,
      getRecordingTimes,
      startMicTest,
      stopMicTest,
      isMicTesting,
      onS3UploadError,
      setOnS3UploadError,
      setOnRecordingFinalized,
      setOnRecordingStopped,
      requestRemoteStop,
      requestRemotePause,
      requestRemoteResume,
      remoteStopPending,
      audioHealthState,
      releaseAudioStream,
      needsMicPermission,
      enableAudioDeviceService,
      enableStorageService,
      enableUploadService,
      enableRecordingService,
      enableSessionCompletionService,
      setRecordingEncounterId,
      getRecordingEncounterId,
      setTelehealthSession,
      cleanupTelehealthSession,
      audioInputDeviceService: exposedAudioDeviceService,
      resetRecordingState,
      pipelineError,
      retryPipelineInit,
      sessionCompletionService: exposedCompletionService,
      sessionRecoveryService: exposedRecoveryService,
      uploadService: exposedUploadService,
      recordingService: exposedRecordingService,
      storageService: exposedStorageService,
    }),
    [
      recordingStatus,
      noteInformation,
      noAudioStatus,
      selectedMicrophone,
      availableMicrophones,
      microphonePermissionGranted,
      elapsedTime.current,
      appointmentType,
      useSharedMedia,
      lowDataMode,
      allowRecorderControl,
      rehydrateStateFromLocalStorage,
      storagePersisted,
      isMicTesting,
      onS3UploadError,
      setOnS3UploadError,
      setOnRecordingFinalized,
      setOnRecordingStopped,
      requestRemoteStop,
      requestRemotePause,
      requestRemoteResume,
      remoteStopPending,
      audioHealthState,
      needsMicPermission,
      resetRecordingState,
      pipelineError,
      retryPipelineInit,
      exposedCompletionService,
      exposedRecoveryService,
      exposedUploadService,
      exposedRecordingService,
      exposedStorageService,
      exposedAudioDeviceService,
    ],
  );

  // Provide the context to children components
  return (
    <RecordingContext.Provider value={value}>
      {children}
      <StorageCleanupDialog
        open={showStorageCleanupDialog}
        onClose={() => setShowStorageCleanupDialog(false)}
        onCleanupComplete={() => {
          // Check storage again after cleaning up
          initiateRecording().catch(error => {
            console.error('Error starting recording after cleanup:', error);
          });
        }}
        storageInfo={
          storageInfo
            ? {
                usagePercent: storageInfo.usagePercent,
                availableMB: storageInfo.availableMB,
                quotaMB: storageInfo.quotaMB,
              }
            : undefined
        }
        minimumRequiredQuotaMB={storageInfo?.minimumRequiredQuotaMB}
        storageService={useStorageServiceRef.current ? storageServiceRef.current : undefined}
      />
    </RecordingContext.Provider>
  );
};

interface UploadResult {
  success: boolean;
  error?: string;
  chunk_data: {
    chunk_size: number;
    chunk_type: string;
  };
}

const uploadAudioAsync = async (
  blob: Blob,
  upload_url: string,
  content_type: string,
  accessToken?: string | null,
  recordingPipeline?: string,
): Promise<UploadResult> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, 45000);

  try {
    // First attempt: Direct S3 PUT
    const options: RequestInit = {
      method: 'PUT',
      body: blob,
      headers: {
        'Content-Type': content_type,
      },
      signal: controller.signal,
    };

    const uploadResponse = await fetch(upload_url, options);

    if (!uploadResponse.ok) {
      throw new Error(`HTTP error! status: ${uploadResponse.status}`);
    }

    console.log(`S3 upload response: ${uploadResponse.status}`);

    return {
      success: true,
      chunk_data: { chunk_size: blob.size, chunk_type: blob.type },
    };
  } catch (primaryError: unknown) {
    let primaryErrorMessage: string;

    if (primaryError instanceof Error) {
      primaryErrorMessage = primaryError.message;
    } else {
      primaryErrorMessage = String(primaryError);
    }

    if (primaryError instanceof DOMException && primaryError.name === 'AbortError') {
      primaryErrorMessage = 'Upload timed out after 45 seconds';
    }

    console.error('Primary S3 upload failed:', primaryErrorMessage);

    // Attempt fallback upload via our API endpoint
    try {
      console.log('Attempting fallback upload via API...');

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('audio', blob, 'chunk.webm'); // Add filename for proper handling
      formData.append('presigned_url', upload_url);
      formData.append('content_type', content_type);
      formData.append('recording_pipeline', recordingPipeline ?? 'unknown');

      // Use the configured API base URL
      const fallbackUrl = `${API_BASE_URL}/v2/provider/transcriptions/proxy-upload`;

      SentryService.addBreadcrumb({
        category: 'audio.upload',
        message: 'Attempting fallback upload via API',
        level: 'warning',
        data: {
          primaryError: primaryErrorMessage,
          fallbackUrl,
          blobSize: blob.size,
          contentType: content_type,
        },
      });

      const headers: HeadersInit = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const fallbackResponse = await fetch(fallbackUrl, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
        headers,
        // Don't set Content-Type header - let browser set it with boundary for multipart
      });

      if (!fallbackResponse.ok) {
        throw new Error(`Fallback HTTP error! status: ${fallbackResponse.status}`);
      }

      console.log(`Fallback upload response: ${fallbackResponse.status}`);

      SentryService.addBreadcrumb({
        category: 'audio.upload',
        message: 'Fallback upload succeeded',
        level: 'info',
        data: { status: fallbackResponse.status },
      });

      return {
        success: true,
        chunk_data: { chunk_size: blob.size, chunk_type: blob.type },
      };
    } catch (fallbackError: unknown) {
      let fallbackErrorMessage: string;

      if (fallbackError instanceof Error) {
        fallbackErrorMessage = fallbackError.message;
      } else {
        fallbackErrorMessage = String(fallbackError);
      }

      if (fallbackError instanceof DOMException && fallbackError.name === 'AbortError') {
        fallbackErrorMessage = 'Fallback upload timed out after 45 seconds';
      }

      console.error('Fallback upload also failed:', fallbackErrorMessage);

      SentryService.logEvent('Both primary and fallback uploads failed', {
        level: 'error',
        extra: {
          primaryError: primaryErrorMessage,
          fallbackError: fallbackErrorMessage,
          uploadUrl: upload_url,
          blobSize: blob.size,
          contentType: content_type,
        },
      });

      // Return the original error since that's what the user would expect
      return {
        success: false,
        error: `Primary upload failed: ${primaryErrorMessage}. Fallback also failed: ${fallbackErrorMessage}`,
        chunk_data: { chunk_size: blob.size, chunk_type: blob.type },
      };
    }
  } finally {
    clearTimeout(timeoutId);
  }
};

// Helper function to add a chunk to the transcription session
export const addChunkToTranscriptionSession = async (
  transcriptionSessionId: string,
  chunkId: string,
  chunkPosition: number,
  s3_object_id: string,
  accessToken?: string | null,
) => {
  const transcriptionSessionResponse = await APIService.makeAPIPostRequest({
    requestString: '/transcriptionSession/addChunkToSession',
    accessToken,
    body: {
      chunk_id: chunkId,
      chunk_position: chunkPosition,
      transcription_session_id: transcriptionSessionId,
      s3_object_id: s3_object_id,
    },
  });

  if (transcriptionSessionResponse.ok) {
    return true;
  } else {
    return false;
  }
};
